<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faSave, faTimes, faArrowDown, faChevronDown, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
  import { Button } from 'flowbite-svelte';
  import CustomImage from '../../common/CustomImage.svelte';
  import { createGrid } from 'ag-grid-community';
  import type { GridApi } from 'ag-grid-community';
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-quartz.css';
  import { theme } from '../../../services/stores/theme-store.js';
  import { projectStructureStore, studyExtractStore, documentExtractStore, documentExtractFullStore } from './stores.js';
  import { documentExtractTableTo2DArray, studyTableTo2DArray, arrayToStudyTable, arrayToReference } from './target-table-processing.js';
  import { referenceTo2DArray } from './data-conversion.js';
  import { convertExtractToWorking, getCurrentVersion, updateCellValue, addRow, addColumn, removeRow, removeColumn, resetToInitial, pivotTable, workingTableTo2DArray } from './source-table-processing.js';
  import { addTargetRow, removeTargetRows, reorderTargetColumns } from './target-table-processing.js';
  import { saveTargetExtract } from '../data/target-data-handler.js';
  import { 
    validateSourceAgainstTarget, 
    getValidationSummary,
    matchOnlySync,
    matchAllSync,
    getSyncSummary,
    buildSourceKeyMapping,
    buildTargetKeyMapping,
    filterRowsBySelection,
    matchOnlySyncWithKeys,
    matchAllSyncWithKeys,
    matchSelectedSyncWithKeys,
    matchOnlySyncTargetToTarget,
    matchAllSyncTargetToTarget,
    matchSelectedSyncTargetToTarget
  } from './table-mapping.js';
  import { ColumnHeaderForTarget, ColumnHeaderForSource } from './headerComponents.js';
  import { loadDataForMode as loadDataForModeExternal, type FlowMode, type DataLoadingContext } from './data-loading.js';
  import { handleMatchSelected as handleMatchSelectedExternal, handleDocumentStudySelected as handleDocumentStudySelectedExternal, handleDocumentStudyMatching as handleDocumentStudyMatchingExternal, handleDocumentStudyAll as handleDocumentStudyAllExternal, getSelectedRowsFromGrid } from './replacementFunctions.js';
  import { preprocessTableOnce } from './source-table-processing.js';
  import { saveWorkingDocument } from '../data/extract-data-handler.js';
  import SourceTableEditor from './SourceTableEditor.svelte';
  import ReferenceGrid from './ReferenceGrid.svelte';
  import {  getCellEditorConfig,  getLookupValueGetterSetter,  getLookupCellRenderer } from './cell-editor-config.js';
  import { logInfo, logError, logWarning } from "../../../services/logging/log-handler.js";
  // References don't use groupers - no grouper imports needed
  
  // Props
  export let show: boolean = false;
  export let pageNumber: number = 5;
  export let topicId: string = 'baseline';
  export let tableId: string = 'age';
  export let referenceId: string = ''; // Optional: when provided, component operates in reference mode
  
  // Force reactivity for show variable
  $: isVisible = show;
  
  // Detect if we're in reference mode (referenceId provided) or topic mode (topicId/tableId provided)
  $: isReferenceMode = !!referenceId && referenceId.trim() !== '';
  $: isTopicMode = !!topicId && topicId.trim() !== '' && !!tableId && tableId.trim() !== '';
  
  // Mode selection state
  let currentMode: FlowMode = 'source-document';
  
  // Grid refs
  let documentTargetGridDiv: HTMLDivElement; // Used in source-document mode and document-study mode
  let studyTargetGridDiv: HTMLDivElement;   // Used in source-study mode and document-study mode
  let sourceGridApi: GridApi | null = null; // Set by SourceTableEditor via onGridReady callback
  let documentTargetGridApi: GridApi | null = null; // Used in source-document mode and document-study mode
  let studyTargetGridApi: GridApi | null = null;   // Used in source-study mode and document-study mode
  let documentTargetGridInstance: any = null; // Used in source-document mode and document-study mode
  let studyTargetGridInstance: any = null;   // Used in source-study mode and document-study mode
  
  // State
  let availableSourceTables: any[] = [];
  let selectedSourceTable: any | null = null;
  let sourceData2D: any[][] = [];
  let documentTargetData2D: any[][] = []; // Used in source-document mode and document-study mode
  let studyTargetData2D: any[][] = [];   // Used in source-study mode and document-study mode
  let originalSourceData: any[][] = [];
  let validationResult: any = null;
  let hasChanges = false;
  let syncSummary = '';
  
  // Working format data
  let workingData: any = null;
  let currentTableIndex: number = 0;
  let isUpdatingCell = false; // Flag to prevent reactive updates during cell editing
  let isUpdatingDocumentTarget = false; // Flag to prevent reactive updates during document target operations (for source-document mode and document-study mode)
  let isUpdatingStudyTarget = false; // Flag to prevent reactive updates during study target operations (for source-study mode and document-study mode)
  let sourceGridInitialized = false; // Flag to track if source grid has been initialized
  let documentTargetGridInitialized = false; // Flag to track if document target grid has been initialized (for source-document mode and document-study mode)
  let studyTargetGridInitialized = false; // Flag to track if study target grid has been initialized (for source-study mode and document-study mode)
  let lastSelectedSourceTable: any | null = null; // Track last selected source table to detect changes
  let lastEffectiveTableId: string = ''; // Track last effectiveTableId to detect changes
  
  // Pivot state - no longer needed since we always pivot
  
  // effectiveTableId is the target we're working with (passed as prop or detected)
  $: effectiveTableId = (() => {
    // In reference mode, effectiveTableId should be the referenceId
    if (isReferenceMode && referenceId) {
      return referenceId;
    }
        
    // If tableId prop is provided, use it (it's the target, e.g., "age", "gender", "subgroups")
    if (tableId && tableId.trim()) {
      return tableId;
    }
    
    // If no tableId prop, try to detect from selectedSourceTable
    if (selectedSourceTable && selectedSourceTable.work && Array.isArray(selectedSourceTable.work) && selectedSourceTable.work.length > 0) {
      const detected = selectedSourceTable.work[0].target;
      return detected;
    }
    
    return ''; // Fallback to empty string
  })();
  
  // Compute display name for table title
  $: tableDisplayName = (() => {
    if (isReferenceMode && referenceId && $projectStructureStore) {
      const referenceStructure = $projectStructureStore.references?.find((r: any) => r.id === referenceId);
      return referenceStructure?.label || referenceId;
    }
          if (isTopicMode && topicId && effectiveTableId && $projectStructureStore) {
      // Grouper editing is handled by separate GrouperEditor component
      const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
      const tableStructure = topicStructure?.tables?.find((t: any) => t.id === effectiveTableId);
      return tableStructure?.label || effectiveTableId;
    }
    return effectiveTableId || '';
  })();
  
  // Get reference column structure for ReferenceGrid
  $: referenceColumnStructure = (() => {
    if (isReferenceMode && referenceId && $projectStructureStore) {
      const referenceStructure = $projectStructureStore.references?.find((r: any) => r.id === referenceId);
      if (referenceStructure) {
        return referenceStructure.columns || referenceStructure.structure?.columns || [];
      }
    }
    return [];
  })();
  
  // Get study target rows from studyTargetData2D for ReferenceGrid
  $: studyTargetRows = (() => {
    if (studyTargetData2D.length === 0) return [];
    if (!$studyExtractStore || !referenceId) return [];
    
    const referenceData = $studyExtractStore.references?.find((r: any) => r.id === referenceId);
    if (!referenceData) return [];
    
    return referenceData.rows || [];
  })();
  
  // Get row ID map for ReferenceGrid (maps rowIndex to rowId from studyTargetData2D)
  $: studyTargetRowIdMap = (() => {
    const map = new Map<number, string>();
    if (studyTargetData2D.length === 0 || !$studyExtractStore || !referenceId) return map;
    
    const referenceData = $studyExtractStore.references?.find((r: any) => r.id === referenceId);
    if (!referenceData || !referenceData.rows) return map;
    
    // Map row index (0-based) to row ID
    referenceData.rows.forEach((row: any, index: number) => {
      if (row.id) {
        map.set(index, row.id);
      }
    });
    
    return map;
  })();
  
  // Helper function to get sourceInfo from study extract store for a specific cell (for indicators)
  function getStudyTargetSourceInfoForCell(rowIndex: number, columnId: string): { sourceType: string; sourceID?: string; sourceName?: string; sourceDate?: string } | null | undefined {
    if (!$studyExtractStore || !referenceId) {
      return null;
    }
    
    // Get row ID from mapping
    const rowId = studyTargetRowIdMap.get(rowIndex);
    if (!rowId) {
      return null;
    }
    
    // Find the reference in the store
    const referenceData = $studyExtractStore.references?.find((r: any) => r.id === referenceId);
    if (!referenceData || !referenceData.rows) {
      return null;
    }
    
    // Find the row
    const row = referenceData.rows.find((r: any) => r.id === rowId);
    if (!row || !row.cells) {
      return null;
    }
    
    // Find the cell
    const cell = row.cells.find((c: any) => c.id === columnId);
    if (!cell) {
      return null;
    }
    
    // Return sourceInfo if available
    return cell.sourceInfo || null;
  }
  
  // Selection state
  let selectedColumns: Set<number> = new Set();
  let selectedRows: Set<number> = new Set();
  let selectedDocumentTargetRows: Set<number> = new Set(); // For document target grid row selection (source-document mode and document-study mode)
  let selectedStudyTargetRows: Set<number> = new Set(); // For study target grid row selection (source-study mode and document-study mode)
  let showColumnAddDropdown = false;
  
  // Grouper editor state (no longer using popup, using inline grid)
  
  // Column popup state moved to SourceTableEditor
  
  // Button state logic
  $: columnAddEnabled = selectedColumns.size === 0;
  $: columnAddIsDropdown = selectedColumns.size === 1;
  $: columnRemoveEnabled = selectedColumns.size > 0;
  $: columnEditEnabled = selectedColumns.size === 1;
  
  $: rowRemoveEnabled = selectedRows.size > 0;
  
  // Replace button state logic
  // Check if at least one column matches between source and target
  // This checks both label matching (from validation) and key-based mapping
  $: hasColumnMatch = (() => {
    // First check validation result (label-based matching)
    if (validationResult && validationResult.columnValidation) {
      const matchedColumns = Array.from(validationResult.columnValidation.values()).filter(v => v === true);
      if (matchedColumns.length > 0) {
        return true;
      }
    }
    
    // If no label matches, check if we have key-based mappings
    // This is important for references where labels may differ but keys match
    if (sourceData2D.length > 0) {
      let targetData: any[][] = [];
      if (currentMode === 'source-document') {
        targetData = documentTargetData2D;
      } else if (currentMode === 'source-study') {
        targetData = studyTargetData2D;
      }
      
      if (targetData.length > 0 && workingData && currentTableIndex >= 0 && effectiveTableId) {
        try {
          // Build key mappings to check if any columns match by key
          const sourceKeyMapping = buildSourceKeyMapping(workingData, currentTableIndex, effectiveTableId, sourceData2D[0]);
          const targetHeaders = currentMode === 'source-document' ? documentTargetData2D[0] : studyTargetData2D[0];
          const targetKeyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, targetHeaders, referenceId);
          
          // Check if any source column has a matching target column by key
          const sourceHeaders = sourceData2D[0];
          for (const sourceHeader of sourceHeaders) {
            const sourceKey = sourceKeyMapping.get(sourceHeader);
            if (sourceKey) {
              // Check if any target header maps to the same key
              for (const targetHeader of targetHeaders) {
                const targetKey = targetKeyMapping.get(targetHeader);
                if (targetKey === sourceKey) {
                  return true; // Found a match by key
                }
              }
            }
          }
        } catch (error) {
          logError(error, "ReferenceTableEditor", undefined, { message: 'Error checking key-based column matching' });
        }
      }
    }
    
    return false;
  })();
  
  $: selectedButtonEnabled = selectedRows.size > 0 && hasColumnMatch; // For source-document and source-study modes
  $: matchingButtonEnabled = hasColumnMatch; // For Matching and All buttons
  $: documentStudySelectedButtonEnabled = selectedDocumentTargetRows.size > 0; // For document-study mode
  
  // Debug logging for button state
  $: if (show) {
  }
  
  // Target table button state logic
  $: documentTargetRowRemoveEnabled = selectedDocumentTargetRows.size > 0; // For document target grid (source-document mode and document-study mode)
  $: studyTargetRowRemoveEnabled = selectedStudyTargetRows.size > 0; // For study target grid (source-study mode and document-study mode)
  
  // Load available source tables from documentExtract for all pages
  $: if (show && $documentExtractStore?.tables) {
    
    // Show all tables from all pages, not just the current page, but only with status valid or none
    const newAvailableTables = ($documentExtractStore.tables || []).filter((t: any) => !t.status || t.status === 'valid');
    
    // If we have a selected table, try to find it in the new list (in case it was updated)
    if (selectedSourceTable && newAvailableTables.length > 0) {
      const matchingTable = newAvailableTables.find((t: any) => 
        t.page === selectedSourceTable.page && t.name === selectedSourceTable.name
      );
      if (matchingTable) {
        // Update to the new reference (in case it was updated during preprocessing)
        selectedSourceTable = matchingTable;
      } else {
        // Selected table no longer available, clear it
        selectedSourceTable = null;
      }
    }
    
    availableSourceTables = newAvailableTables;
    
    // Auto-select the first table if none is selected
    if (availableSourceTables.length > 0 && !selectedSourceTable) {
      selectedSourceTable = availableSourceTables[0];
    }
  } else {
  }

  // When a source table is selected, ensure it is preprocessed if needed, then persist
  $: if (show && selectedSourceTable && $documentExtractStore) {
    try {
      // Store the current selection to preserve it during preprocessing
      const selectedPage = selectedSourceTable.page;
      const selectedName = selectedSourceTable.name;
      const key = `${selectedPage}::${selectedName}`;
      if (!selectedSourceTable.status) {
        // Find and preprocess this table only
        const ix = $documentExtractStore.tables.findIndex((t: any) => t.page === selectedPage && t.name === selectedName);
        if (ix >= 0) {
          const processed = preprocessTableOnce($documentExtractStore.tables[ix]);
          
          // Update documentExtractStore
          const updatedDoc = JSON.parse(JSON.stringify($documentExtractStore));
          updatedDoc.tables[ix] = processed;
          documentExtractStore.set(updatedDoc);
          
          // The reactive statement for availableSourceTables will handle restoring the selected table
          // by finding the matching table by page/name after the store update
          
          // Also ensure work section exists and update workingData (only if we have a target)
          // Compute effectiveTableId inside to avoid cyclical dependency - use referenceId for reference mode
          const computedEffectiveTableIdForPreprocess = (isReferenceMode && referenceId) ? referenceId : (tableId && tableId.trim() ? tableId : '');
          if (workingData && computedEffectiveTableIdForPreprocess) {
            const workTableIx = workingData.tables.findIndex((t: any) => t.page === selectedSourceTable.page && t.name === selectedSourceTable.name);
            if (workTableIx >= 0) {
              // Update the table in workingData to match preprocessed version
              workingData.tables[workTableIx] = JSON.parse(JSON.stringify(processed));
              const workTable = workingData.tables[workTableIx];
              
              // Ensure work array exists
              if (!Array.isArray(workTable.work)) {
                workTable.work = [];
              }
              
              // Check if work entry for this target already exists
              const existingWorkIndex = workTable.work.findIndex((w: any) => w?.target === computedEffectiveTableIdForPreprocess);
              
              if (existingWorkIndex < 0) {
                // Create new work entry from preprocessed data
                const newWorkEntry = {
                  target: computedEffectiveTableIdForPreprocess,
                  next_row: `r${(processed.data?.rows?.length || 0) + 1}`,
                  next_col: `c${(processed.data?.columns?.length || 0) + 1}`,
                  columns: JSON.parse(JSON.stringify(processed.data?.columns || [])),
                  rows: JSON.parse(JSON.stringify(processed.data?.rows || []))
                };
                workTable.work.push(newWorkEntry);
              } else {
                // Update existing work entry verifying preprocessed data
                workTable.work[existingWorkIndex] = {
                  ...workTable.work[existingWorkIndex],
                  columns: JSON.parse(JSON.stringify(processed.data?.columns || [])),
                  rows: JSON.parse(JSON.stringify(processed.data?.rows || []))
                };
              }
              
              saveWorkingData();
            } else {
              logWarning('[SOURCE-PREPROCESS] Table not found in workingData for page:', "ReferenceTableEditor", undefined, { page: selectedSourceTable.page, name: selectedSourceTable.name });
            }
          } else {
            logWarning('[SOURCE-PREPROCESS] Skipping work section creation - workingData:', "ReferenceTableEditor", undefined, { workingData: !!workingData, computedEffectiveTableId: computedEffectiveTableIdForPreprocess });
          }
          
          // Persist to storage (using same name identifier as ExtractItems2)
          saveWorkingDocument('doc1', updatedDoc);
          console.groupEnd();
          // Update selection reference to the processed table
          selectedSourceTable = processed;
        }
      }
    } catch (e) {
      logError(e, "ReferenceTableEditor", undefined, { message: '[SOURCE-PREPROCESS] Error while preprocessing selected table' });
    }
  }

  // Separate reactive block to ensure work section exists after preprocessing when workingData is available
  $: if (show && selectedSourceTable && selectedSourceTable.status === 'valid' && workingData && tableId && $documentExtractStore) {
    try {
      // Compute effectiveTableId inside the block to avoid cyclical dependency
      const computedEffectiveTableId = tableId && tableId.trim() ? tableId : '';
      if (computedEffectiveTableId) {
        const key = `${selectedSourceTable.page}::${selectedSourceTable.name}`;
        // Check if this table needs a work entry
        const workTableIx = workingData.tables.findIndex((t: any) => t.page === selectedSourceTable.page && t.name === selectedSourceTable.name);
        if (workTableIx >= 0) {
          const workTable = workingData.tables[workTableIx];
          // Check if work entry exists for this target
          const hasWorkEntry = Array.isArray(workTable.work) && workTable.work.find((w: any) => w?.target === computedEffectiveTableId);
          if (!hasWorkEntry) {
            
            // Get the preprocessed table from documentExtractStore
            const docTable = $documentExtractStore.tables.find((t: any) => t.page === selectedSourceTable.page && t.name === selectedSourceTable.name);
            if (docTable && docTable.status === 'valid' && docTable.data) {
              // Update workingData table to match preprocessed version
              workingData.tables[workTableIx] = JSON.parse(JSON.stringify(docTable));
              const updatedTable = workingData.tables[workTableIx];
              
              // Ensure work array exists
              if (!Array.isArray(updatedTable.work)) {
                updatedTable.work = [];
              }
              
              // Create work entry from preprocessed data
              const newWorkEntry = {
                target: computedEffectiveTableId,
                next_row: `r${(updatedTable.data?.rows?.length || 0) + 1}`,
                next_col: `c${(updatedTable.data?.columns?.length || 0) + 1}`,
                columns: JSON.parse(JSON.stringify(updatedTable.data?.columns || [])),
                rows: JSON.parse(JSON.stringify(updatedTable.data?.rows || []))
              };
              updatedTable.work.push(newWorkEntry);
              saveWorkingData();
            }
          }
      }
      }
    } catch (e) {
      logError(e, "ReferenceTableEditor", undefined, { message: 'Error ensuring work section' });
    }
  }

  // Load or create working data when editor opens or page/topic/table/mode changes
  let lastPageNumber: number | undefined = undefined;
  let lastTopicId: string | undefined = undefined;
  let lastTableId: string | undefined = undefined;
  let lastReferenceId: string | undefined = undefined;
  let lastMode: FlowMode | undefined = undefined;
  
  $: if (show && $documentExtractStore) {
    // Only reset initialization flags if page/topic/table/reference/mode changed, not on every open/close
    const pageChanged = lastPageNumber !== pageNumber;
    const topicChanged = lastTopicId !== topicId;
    const tableChanged = lastTableId !== tableId;
    const referenceChanged = lastReferenceId !== referenceId;
    const modeChanged = lastMode !== currentMode;
    
    if (pageChanged || topicChanged || tableChanged || referenceChanged) {
      sourceGridInitialized = false;
      documentTargetGridInitialized = false;
      studyTargetGridInitialized = false;
      
      lastPageNumber = pageNumber;
      lastTopicId = topicId;
      lastTableId = tableId;
      lastReferenceId = referenceId;
    }
    
    if (modeChanged) {
      // Reset the appropriate target grid flag based on the new mode
      if (currentMode === 'source-document') {
        documentTargetGridInitialized = false;
      } else if (currentMode === 'source-study') {
        studyTargetGridInitialized = false;
      } else if (currentMode === 'document-study') {
        documentTargetGridInitialized = false;
        studyTargetGridInitialized = false;
      }
      
      lastMode = currentMode;
    }
    
    loadWorkingData();
  }

  // Load target data for source-document mode
  $: if (show && $documentExtractStore && $documentExtractFullStore && $studyExtractStore && $projectStructureStore && 
      ((isReferenceMode && referenceId) || (isTopicMode && topicId && effectiveTableId)) && 
      currentMode === 'source-document' && !documentTargetGridInitialized && !isUpdatingDocumentTarget) {
    loadDataForMode();
  }

  // Initialize document target grid when div is ready (source-document mode)
  $: if (show && currentMode === 'source-document' && documentTargetGridDiv && documentTargetData2D.length > 0 && !documentTargetGridInitialized && !isUpdatingDocumentTarget) {
    setTimeout(() => initializeTargetGrid(), 0);
  }

  // Load target data for source-study mode
  $: if (show && $documentExtractStore && $documentExtractFullStore && $studyExtractStore && $projectStructureStore && 
      ((isReferenceMode && referenceId) || (isTopicMode && topicId && effectiveTableId)) && 
      currentMode === 'source-study' && !studyTargetGridInitialized && !isUpdatingStudyTarget) {
    loadDataForMode();
  }

  // Initialize study target grid when div is ready (source-study mode)
  $: if (show && currentMode === 'source-study' && studyTargetGridDiv && studyTargetData2D.length > 0 && !studyTargetGridInitialized && !isUpdatingStudyTarget) {
    setTimeout(() => initializeTargetGrid(), 0);
  }

  // Initialize document target grid when div is ready (document-study mode)
  $: if (show && currentMode === 'document-study' && documentTargetGridDiv && documentTargetData2D.length > 0 && !documentTargetGridInitialized && !isUpdatingDocumentTarget) {
    setTimeout(() => initializeDocumentTargetGrid(), 0);
  }

  // Initialize study target grid when div is ready (document-study mode)
  // DISABLED: Study target grid is now handled by ReferenceGrid component
  // $: if (show && currentMode === 'document-study' && studyTargetGridDiv && studyTargetData2D.length > 0 && !studyTargetGridInitialized && !isUpdatingStudyTarget) {
  //   setTimeout(() => initializeStudyTargetGrid(), 0);
  // }

  function loadWorkingData() {
    const storageKey = `workingData_${pageNumber}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        workingData = JSON.parse(stored);
        
        // Check if we need to merge additional tables from documentExtractStore
        if ($documentExtractStore?.tables && $documentExtractStore.tables.length > 0 && workingData?.tables) {
          const needsMerge = $documentExtractStore.tables.some((table: any) => {
            const exists = workingData.tables.find((wt: any) => 
              wt.page === table.page && wt.name === table.name
            );
            return !exists;
          });
          
          if (needsMerge) {
            
            // Add any missing tables
            $documentExtractStore.tables.forEach((table: any) => {
              const exists = workingData.tables.find((wt: any) => 
                wt.page === table.page && wt.name === table.name
              );
              if (!exists) {
                workingData.tables.push(JSON.parse(JSON.stringify(table))); // Deep copy
              }
            });
            
          }
        }
        
        // References don't use groupers - no key assignment needed
        // Note: Work sections are created in the preprocessing block when a table is selected
        saveWorkingData();
      } catch (error) {
        logError(error, "ReferenceTableEditor", undefined, { message: "Error parsing stored working data" });
        createWorkingData();
      }
    } else {
      createWorkingData();
    }
  }

  function createWorkingData() {
    // Check if we already have working data structure
    if ($documentExtractStore?.tables?.[0]?.data && $documentExtractStore?.tables?.[0]?.work) {
      workingData = $documentExtractStore;
    } else {
      // Convert extract format to working format
      workingData = convertExtractToWorking($documentExtractStore);
    }
    
    // Merge additional tables from documentExtractStore if they exist
    if ($documentExtractStore?.tables && $documentExtractStore.tables.length > 1 && workingData?.tables) {
      
      // Add any tables from documentExtractStore that aren't in workingData
      $documentExtractStore.tables.forEach((table: any) => {
        const exists = workingData.tables.find((wt: any) => 
          wt.page === table.page && wt.name === table.name
        );
        if (!exists) {
          workingData.tables.push(table);
        }
      });
      
    }
    
    // Note: Work sections are created in the preprocessing block when a table is selected
    
    // References don't use groupers - no key assignment needed
    
    saveWorkingData();
  }
  
  // References don't use groupers - no assignGrouperKeys function needed

  function saveWorkingData() {
    if (workingData) {
      const storageKey = `workingData_${pageNumber}`;
      localStorage.setItem(storageKey, JSON.stringify(workingData));
      // Persist via handler for document-level storage as well
      saveWorkingDocument('doc1', workingData);
    }
  }
  
  // Column popup functions moved to SourceTableEditor
  
  // Function to load source data when table is selected
  function loadSourceData() {
    
    if (!selectedSourceTable || !workingData || !effectiveTableId || isUpdatingCell || !show) {
      return;
    }
    
    // Find the corresponding table in working data
    // Use flexible matching to handle slight differences in table names (e.g., .* suffix)
    const workingTable = workingData.tables.find((t: any) => {
      const pageMatch = t.page === selectedSourceTable.page;
      const tName = t.name || t.table_name;
      const sName = selectedSourceTable.name;
      const nameMatch = tName === sName || 
                       tName === sName + '.*' ||
                       sName === tName + '.*';
      return pageMatch && nameMatch;
    });
    
    if (workingTable) {
      currentTableIndex = workingData.tables.indexOf(workingTable);
      
      let currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
      
      if (!currentData) {
        // Create new entry - use preprocessed data from selectedSourceTable if available
        let sourceData = null;
        
        // Check if selectedSourceTable has preprocessed data (status === 'valid' and has data.columns)
        if (selectedSourceTable && selectedSourceTable.status === 'valid' && selectedSourceTable.data && selectedSourceTable.data.columns) {
          sourceData = selectedSourceTable.data;
        } else if (workingTable.data && workingTable.data.columns) {
          // Use workingTable.data if it has columns (preprocessed)
          sourceData = workingTable.data;
        } else if (workingTable.data) {
          // Fallback to workingTable.data even if not preprocessed
          sourceData = workingTable.data;
        }
        
        if (sourceData && workingTable && workingTable.work && Array.isArray(workingTable.work)) {
          // Deep copy data
          const newCurrentEntry = JSON.parse(JSON.stringify(sourceData));
          // Set target property
          newCurrentEntry.target = effectiveTableId;
          
          // Clean up column labels: remove periods completely
          if (newCurrentEntry.columns && Array.isArray(newCurrentEntry.columns)) {
            newCurrentEntry.columns.forEach((col: any) => {
              if (col.label) {
                col.label = col.label.replace(/\./g, '');
              }
            });
          }
          
          // Update workingTable.data to match preprocessed version if we used preprocessed data
          if (sourceData === selectedSourceTable.data && selectedSourceTable.status === 'valid') {
            workingTable.data = JSON.parse(JSON.stringify(selectedSourceTable.data));
            workingTable.status = 'valid';
          }
          
          // Remove any existing entries with same target (clean up)
          const existingIndex = workingTable.work.findIndex((entry: any) => entry.target === effectiveTableId);
          if (existingIndex >= 0) {
            workingTable.work.splice(existingIndex, 1);
          }
          
          // Add new entry to work array
          workingTable.work.push(newCurrentEntry);
          currentData = newCurrentEntry;
          
          // Save the updated working data
          saveWorkingData();
        }
      }
      
      if (currentData) {
        const newSourceData2D = workingTableTo2DArray(currentData);
        
        // Update sourceData2D - this will trigger SourceTableEditor's reactive statement
        sourceData2D = newSourceData2D;
        originalSourceData = JSON.parse(JSON.stringify(sourceData2D));
        hasChanges = false;
        
        // Reset grid initialization flag to force re-initialization with new data
        sourceGridInitialized = false;
        
        // Source grid will be initialized by SourceTableEditor component
        // We just set up the data here
      } else {
        // If no currentData, clear sourceData2D and reset grid
        sourceData2D = [];
        sourceGridInitialized = false;
      }
    }
  }
  
  // Load source data when table selected (only for source grid, not target)
  // Reset and reload source grid when selectedSourceTable or effectiveTableId changes
  $: if (selectedSourceTable && workingData && !isUpdatingCell && show && effectiveTableId) {
    
    // Check if the selected table or effectiveTableId actually changed
    const tableChanged = lastSelectedSourceTable !== selectedSourceTable;
    const targetChanged = lastEffectiveTableId !== effectiveTableId;
    const somethingChanged = tableChanged || targetChanged;
    
    // Initialize on first load
    const isFirstLoad = !lastSelectedSourceTable && !lastEffectiveTableId;
    
    if (somethingChanged || isFirstLoad) {
      if (sourceGridInitialized) {
        sourceGridInitialized = false;
      }
      
      lastSelectedSourceTable = selectedSourceTable;
      lastEffectiveTableId = effectiveTableId;
      
      // Now load the data
      loadSourceData();
    }
  }
  
  // Source grid initialization is now handled by SourceTableEditor component
  // Run validation when source grid is ready
  $: if (show && sourceData2D.length > 0 && sourceGridInitialized) {
    runValidation();
  }
  
  // Load target data for source-document and source-study modes
  // DISABLED: This reactive statement was causing conflicts with manual updates
  // NOTE: Now using separate reactive statements for each mode with mode-specific flags (isUpdatingDocumentTarget and isUpdatingStudyTarget)
  // $: if (show && $documentExtractStore && $documentExtractFullStore && $studyExtractStore && $projectStructureStore && topicId && tableId && currentMode === 'source-document' && !isUpdatingDocumentTarget) {
  //   
  //   const context: DataLoadingContext = {
  //     documentExtractStore: $documentExtractStore,
  //     documentExtractFullStore: $documentExtractFullStore,
  //     studyExtractStore: $studyExtractStore,
  //     projectStructureStore: $projectStructureStore,
  //     topicId,
  //     tableId
  //   };
  //   
  //   targetData2D = loadDataForModeExternal(currentMode, context) as any[][];
  //   
  //   // Initialize target grid
  //   if (show && targetGridDiv) {
  //     setTimeout(() => initializeTargetGrid(), 100);
  //   }
  // }
  
  // Load document and study data for document-study mode
  $: if (show && $documentExtractStore && $documentExtractFullStore && $studyExtractStore && $projectStructureStore && 
      ((isReferenceMode && referenceId) || (isTopicMode && topicId && effectiveTableId)) && 
      currentMode === 'document-study' && (!documentTargetGridInitialized || !studyTargetGridInitialized) && !isUpdatingDocumentTarget && !isUpdatingStudyTarget) {
    
    const context: DataLoadingContext = {
      documentExtractStore: $documentExtractStore,
      documentExtractFullStore: $documentExtractFullStore,
      studyExtractStore: $studyExtractStore,
      projectStructureStore: $projectStructureStore,
      topicId: isReferenceMode ? '' : topicId,
      tableId: isReferenceMode ? '' : effectiveTableId,
      referenceId: isReferenceMode ? referenceId : undefined
    };
    
    const result = loadDataForModeExternal('document-study', context);
    const { documentData, studyData } = result as { documentData: any[][], studyData: any[][] };
    
    documentTargetData2D = documentData;
    studyTargetData2D = studyData;
    
    // Grid initialization will be handled by reactive statements
  }
  
  // Run validation when both source and target data are available
  // DISABLED: This was causing grid refreshes during cell editing
  
  function runValidation() {
    // Determine which target data to use based on current mode
    let targetData: any[][] = [];
    if (currentMode === 'source-document') {
      targetData = documentTargetData2D;
    } else if (currentMode === 'source-study') {
      targetData = studyTargetData2D;
    }
    
    if (sourceData2D.length > 0 && targetData.length > 0) {
      // Find the key column for validation
      // First, try to find which source column maps to the key column in the target
      let keyColumnLabels: string[] = ['Group']; // Default fallback
      
      if (workingData && currentTableIndex >= 0 && effectiveTableId && $projectStructureStore && sourceData2D.length > 0) {
        // Build key mappings to find the key column
        const sourceHeaders = sourceData2D[0];
        const sourceKeyMapping = buildSourceKeyMapping(workingData, currentTableIndex, effectiveTableId, sourceHeaders);
        const targetHeaders = targetData[0];
        const targetKeyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, targetHeaders, referenceId);
        
        // Find the key column in target (usually the first column or one marked as key)
        // For references, use the first target column as the key column
        let targetKeyColumnLabel = targetHeaders[0];
        let targetKeyColumnKey = targetKeyMapping.get(targetHeaders[0]);
        
        // Try to find which source column maps to this key
        const sourceKeyColumnLabel = Array.from(sourceKeyMapping.entries()).find(([label, key]) => key === targetKeyColumnKey)?.[0];
        
        if (sourceKeyColumnLabel) {
          // For reference tables, column labels may differ between source and target
          // We need to pass both labels - validation will use source label for source indices
          // and target label for target indices
          keyColumnLabels = [sourceKeyColumnLabel];
          
          // For reference tables, we need to find the target column by key, not by label
          // The validation function expects labels that exist in both headers, but for references
          // we need to modify it to accept separate source/target labels or indices
          // For now, let's pass both source and target labels in the array
          // We'll modify validation to handle this case
          const targetKeyColumnLabel = Array.from(targetKeyMapping.entries()).find(([label, key]) => key === targetKeyColumnKey)?.[0];
          if (targetKeyColumnLabel && targetKeyColumnLabel !== sourceKeyColumnLabel) {
            // Pass both labels - validation will use first for source, second for target
            keyColumnLabels = [sourceKeyColumnLabel, targetKeyColumnLabel];
          }
        } else {
          // Fallback: use first column if available
          if (targetHeaders.length > 0) {
            keyColumnLabels = [targetHeaders[0]];
          }
        }
      }
      
      validationResult = validateSourceAgainstTarget(sourceData2D, targetData, keyColumnLabels);
      syncSummary = getSyncSummary(sourceData2D, targetData, keyColumnLabels);
      
      // Update row match status in workingData
      if (validationResult && validationResult.rowValidation && workingData && currentTableIndex >= 0) {
        const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
        if (currentData && (currentData as any).rows) {
          validationResult.rowValidation.forEach((isMatched: boolean, rowIndex: number) => {
            // rowIndex from validation is 1-based (row 0 is headers)
            const actualRowIndex = rowIndex - 1;
            if (actualRowIndex >= 0 && actualRowIndex < (currentData as any).rows.length) {
              (currentData as any).rows[actualRowIndex].match = isMatched;
            }
          });
          saveWorkingData();
          // Update sourceData2D to reflect match status changes
          // This will trigger SourceTableEditor's reactive statement to refresh the grid
          const updatedCurrentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
          if (updatedCurrentData && workingData.tables[currentTableIndex]) {
            sourceData2D = workingTableTo2DArray(updatedCurrentData);
          }
        }
      }
    }
  }

  // Source grid initialization is now handled by SourceTableEditor component
  // This function is kept for backward compatibility but is no longer used
  function initializeSourceGrid() {
  }
  
  // Callback to receive sourceGridApi from SourceTableEditor
  function handleSourceGridReady(api: GridApi) {
    sourceGridApi = api;
  }
  
  // Callback to handle selection changes from SourceTableEditor
  function handleSourceSelectionChanged(newSelectedRows: Set<number>) {
    selectedRows = newSelectedRows;
  }
  
  function initializeTargetGrid() {
    // Determine which grid to initialize based on current mode
    const isDocumentMode = currentMode === 'source-document';
    const gridDiv = isDocumentMode ? documentTargetGridDiv : studyTargetGridDiv;
    const targetData = isDocumentMode ? documentTargetData2D : studyTargetData2D;
    const gridInitialized = isDocumentMode ? documentTargetGridInitialized : studyTargetGridInitialized;
    
    if (!gridDiv || targetData.length === 0) {
      return;
    }
    
    // Destroy existing grid
    if (isDocumentMode) {
      if (documentTargetGridInstance) {
        documentTargetGridInstance.destroy();
        documentTargetGridInstance = null;
        documentTargetGridApi = null;
      }
    } else {
      if (studyTargetGridInstance) {
        studyTargetGridInstance.destroy();
        studyTargetGridInstance = null;
        studyTargetGridApi = null;
      }
    }
    
    // Convert target data to grid format (no special handling for grouper - use GrouperEditor component instead)
    const result = convertToGridFormat(targetData, true, true);
    const columnDefs = result.columnDefs;
    const rowData = result.rowData;
    
    const gridOptions = {
       columnDefs: columnDefs as any,
       rowData,
       domLayout: 'normal' as const, 
       suppressCellFocus: false, 
       // Don't set fixed rowHeight when autoHeight is enabled (for multiline support)
       // rowHeight will be determined by autoHeight in column definitions
       headerHeight: 40,
       autoSizeStrategy: {
         type: 'fitCellContents',
       },
       suppressColumnVirtualisation: true,
       suppressRowVirtualisation: true,
       suppressMovableColumns: true,
       suppressContextMenu: true,
       rowSelection: {
         mode: 'multiRow',
         checkboxes: true,
         headerCheckbox: true,
         enableClickSelection: true
       },
       // Register custom header component for target columns
       components: {
         columnHeaderForTarget: ColumnHeaderForTarget
       },
       defaultColDef: {
         editable: true, // Enable cell editing
         resizable: false, // Disable column resizing
         sortable: false,
         filter: false,
         suppressHeaderMenuButton: true,
         suppressHeaderContextMenu: true
       },
       onGridReady: (params: any) => {
         if (isDocumentMode) {
           documentTargetGridApi = params.api;
         } else {
           studyTargetGridApi = params.api;
         }
       },
       onSelectionChanged: (params: any) => {
         const selectedNodes = params.api.getSelectedNodes();
         const newSelectedRows = new Set<number>();
         
         selectedNodes.forEach((node: any) => {
           if (node.rowIndex !== null && node.rowIndex !== undefined) {
             newSelectedRows.add(node.rowIndex);
           }
         });
         
         if (isDocumentMode) {
           selectedDocumentTargetRows = newSelectedRows;
         } else {
           selectedStudyTargetRows = newSelectedRows;
         }
       },
      onCellValueChanged: (params: any) => {
        // References don't use groupers - all columns are editable
        // Use params.data[colDef.field] to get the value after valueSetter conversion (handles lookup ID conversion)
        const { rowIndex, colDef } = params;
        const cellValue = params.data[colDef.field]; // This has the ID after valueSetter conversion
        
        const targetData = isDocumentMode ? documentTargetData2D : studyTargetData2D;
        const headers = targetData[0];
        const colIndex = headers.indexOf(colDef.field);
        
        if (colIndex >= 0 && rowIndex >= 0) {
          const actualRowIndex = rowIndex + 1; // Account for header row
          if (actualRowIndex < targetData.length) {
            targetData[actualRowIndex][colIndex] = cellValue;
            
            // Update the store
            if (isDocumentMode) {
              updateDocumentExtractStore(documentTargetData2D);
            } else {
              updateStudyExtractStore(studyTargetData2D);
            }
            
          }
        }
      },
     };
    
    if (isDocumentMode) {
      documentTargetGridInstance = createGrid(gridDiv, gridOptions as any);
      documentTargetGridInitialized = true; // Mark as initialized after grid creation
    } else {
      studyTargetGridInstance = createGrid(gridDiv, gridOptions as any);
      studyTargetGridInitialized = true; // Mark as initialized after grid creation
    }
    
  }
  
  function convertToGridFormat(data2D: any[][], includeCheckboxes = false, isTargetGrid = false): any {
    if (data2D.length === 0) return { columnDefs: [], rowData: [] };
    
    const headers = data2D[0];
    const rows = data2D.slice(1);
    
    const columnDefs: any[] = [];
    
    // Get the current working data to access column properties (only for source grids)
    const currentData = !isTargetGrid ? getCurrentVersion(workingData, currentTableIndex, effectiveTableId) : null;
    
    // Add match icon column for source grids (positioned right after checkbox column)
    // Use unshift() to add it early, and AG Grid will place it after the auto-created checkbox column
    if (!isTargetGrid && includeCheckboxes) {
      columnDefs.unshift({
        field: '_match',
        headerName: '',
        width: 28, // Narrow width for icon column
        minWidth: 28, // Ensure it stays narrow
        maxWidth: 28, // Prevent expansion
        cellClass: 'match-icon-cell', // Custom class to override padding
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        resizable: false,
        sortable: false,
        filter: false,
        editable: false,
        lockPosition: true,
        suppressMovable: true,
        cellRenderer: (params: any) => {
          let sp = '<span style="display: inline-flex; align-items: center; justify-content: center; width: 100%; height: 100%;"';
          if (params.value === true || params.value === 'true') {
            sp = sp + 'title="Row is mapped"><svg class="w-5 h-5 match-icon" viewBox="0 0 640 640"><path fill="currentColor" d="M320 576C178.6 576 64 461.4 64 320C64 178.6 178.6 64 320 64C461.4 64 576 178.6 576 320C576 461.4 461.4 576 320 576zM438 209.7C427.3 201.9 412.3 204.3 404.5 215L285.1 379.2L233 327.1C223.6 317.7 208.4 317.7 199.1 327.1C189.8 336.5 189.7 351.7 199.1 361L271.1 433C276.1 438 282.9 440.5 289.9 440C296.9 439.5 303.3 435.9 307.4 430.2L443.3 243.2C451.1 232.5 448.7 217.5 438 209.7z"/></svg></span>';
            return sp;  
          } else if (params.value === false || params.value === 'false') {
            sp = sp +  'title="Row is not mapped"><svg class="w-5 h-5 no-match-icon" viewBox="0 0 20 20"><path fill="currentColor" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg></span>';
            return sp;
          }
          return sp + '></span>';
        }
      });
    }
    
    // Add data columns with custom header components
    headers.forEach((header: string, index: number) => {
      let columnKey = '-';
      let isMapped = false;
      
      if (!isTargetGrid) { // Logic for source grid
      const workingColumn = (currentData as any)?.columns?.find((col: any) => col.label === header);
        columnKey = workingColumn?.key || '-';
        isMapped = workingColumn?.match || false;
      } else { // Logic for target grid
        if (isReferenceMode) {
          // Reference mode: use reference structure
          if ($projectStructureStore && referenceId) {
            const referenceStructure = $projectStructureStore.references?.find((r: any) => r.id === referenceId);
            if (referenceStructure) {
              // Reference structure can have columns directly or in structure.columns
              const columns = referenceStructure.columns || referenceStructure.structure?.columns || [];
              // Match by label first (header should be the label from referenceTo2DArray), then fall back to ID
              const columnStructure = columns.find((col: any) => col.label === header || col.id === header);
              if (columnStructure) {
                columnKey = columnStructure.id;
                // Ensure header is the label, not the ID (should already be from referenceTo2DArray)
                // But if header matched by ID, we need to use the label
                if (columnStructure.label && header === columnStructure.id) {
                  // Header was the ID, but we want to display the label
                  // Note: We can't change the header here as it's used for field matching
                  // Instead, we'll pass the correct label via headerComponentParams
                }
              }
            }
          }
        }
        // ReferenceTableEditor is only for references - no topic mode logic needed
      }
      
      // For target grids in reference mode, ensure we have the correct label and ID
      let displayLabel = header;
      let displayKey = columnKey;
      let isMultiline = false;
      let columnStructure: any = null;
      
      if (isTargetGrid && isReferenceMode && $projectStructureStore && referenceId) {
        const referenceStructure = $projectStructureStore.references?.find((r: any) => r.id === referenceId);
        if (referenceStructure) {
          const columns = referenceStructure.columns || referenceStructure.structure?.columns || [];
          // Try multiple matching strategies: by label, by ID, or by columnKey
          columnStructure = columns.find((col: any) => 
            col.label === header || 
            col.id === header || 
            col.id === columnKey ||
            col.label === columnKey
          );
          if (columnStructure) {
            displayLabel = columnStructure.label || columnStructure.id || header;
            displayKey = columnStructure.id || columnKey;
            isMultiline = columnStructure.multiline === true;
          }
        }
      } else if (isTargetGrid && !isReferenceMode && $projectStructureStore && topicId && effectiveTableId) {
        // Topic mode: get column structure for editor config
        const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
        if (topicStructure) {
          const tableStructure = topicStructure.tables?.find((t: any) => t.id === effectiveTableId);
          if (tableStructure) {
            // Try multiple matching strategies: by label, by ID, or by columnKey
            columnStructure = tableStructure.columns?.find((col: any) => 
              col.label === header || 
              col.id === header ||
              col.id === columnKey ||
              col.label === columnKey
            );
            if (columnStructure) {
              displayLabel = columnStructure.label || columnStructure.id || header;
              displayKey = columnStructure.id || columnKey;
              isMultiline = columnStructure.multiline === true;
            }
          }
        }
      }
      
      const isFirstColumn = index === 0;
      const isFixed = isFirstColumn;
      
      // References don't use groupers - no grouper column checks needed
      // All columns are editable for references
      
      // For source grids, check if we can get column structure from target
      // This helps when source columns are mapped to target columns
      if (!isTargetGrid && currentData && isReferenceMode && referenceId) {
        const workingColumn = currentData.columns?.find((col: any) => col.label === header);
        if (workingColumn?.key && $projectStructureStore) {
          const referenceStructure = $projectStructureStore.references?.find((r: any) => r.id === referenceId);
          if (referenceStructure) {
            const columns = referenceStructure.columns || referenceStructure.structure?.columns || [];
            const targetColumn = columns.find((col: any) => col.id === workingColumn.key);
            if (targetColumn) {
              columnStructure = targetColumn;
              isMultiline = targetColumn.multiline === true;
            }
          }
        }
      }
      
      // Use columnStructure if available, otherwise create a minimal column object for editor config
      const columnForEditor = columnStructure || { id: displayKey, label: displayLabel, multiline: isMultiline };
      
      // Get cell editor configuration using shared utility
      const editorConfig = getCellEditorConfig(columnForEditor, $projectStructureStore);
      
      // Get lookup value getter/setter if this is a lookup column
      const lookupValueConfig = getLookupValueGetterSetter(columnForEditor, $projectStructureStore);
      
      // Get lookup cell renderer if this is a lookup column
      const lookupRenderer = getLookupCellRenderer(columnForEditor, $projectStructureStore);
      
      columnDefs.push({
        field: header,
        headerName: displayLabel, // Use label for display
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        headerComponent: isTargetGrid ? 'columnHeaderForTarget' : 'columnHeaderForSource',
        headerComponentParams: {
          columnIndex: index,
          columnName: displayLabel,
          displayName: displayLabel, // Explicitly pass displayName for custom header component
          key: displayKey,
          isMapped: isMapped,
          onEditColumn: (colIndex: number, event?: MouseEvent) => {
            // Handled by SourceTableEditor internally
          },
          onDeleteColumn: (colIndex: number) => {
            // Handled by SourceTableEditor internally
          }
        },
        // Multiline support for both source and target grids (like ExtractItems2)
        wrapText: true,
        autoHeight: true,
        editable: true, // All columns are editable for references
        ...editorConfig,
        // Set widths based on column type
        // Multiline and fixed columns get fixed width
        // Regular columns get minWidth and let autoSizeStrategy handle sizing
        width: isMultiline ? 200 : (isFixed ? 150 : undefined),
        minWidth: isMultiline ? 200 : (isFixed ? 150 : 80), // Minimum width for regular columns
        maxWidth: isMultiline ? 200 : (isFixed ? 150 : undefined), // No max for regular columns to allow auto-sizing
        suppressSizeToFit: isMultiline || isFixed,
        suppressAutoSize: isMultiline || isFixed,
        cellStyle: {
          'white-space': 'normal',
          'word-wrap': 'break-word',
          'overflow-wrap': 'break-word',
          'padding': '8px 8px',
          'line-height': 'normal',
          'vertical-align': 'top'
        },
        // Convert "-" to "" for editing, "" to "-" for display
        // For lookup columns, use the shared utility's valueGetter/valueSetter
        valueGetter: (params: any) => {
          const value = params.data[header];
          if (value === '-' || value === undefined || value === null) return '';
          
          // Use lookup value getter if available
          if (lookupValueConfig?.valueGetter) {
            return lookupValueConfig.valueGetter(params);
          }
          
          return value;
        },
        valueSetter: (params: any) => {
          let value = params.newValue === '' || params.newValue === null || params.newValue === undefined ? '-' : params.newValue;
          
          // Use lookup value setter if available
          if (lookupValueConfig?.valueSetter) {
            return lookupValueConfig.valueSetter(params);
          }
          
          params.data[header] = value;
          return true;
        },
        cellRenderer: (params: any) => {
          // For lookup columns, use the lookup renderer
          if (lookupRenderer) {
            return lookupRenderer(params);
          }
          // Just show the data, no checkboxes in cells
          return params.value || '-';
        }
      });
    });
    
    const rowData: any[] = [];
    
    // No need for column selection row - checkboxes are now in headers
    
    // Add data rows
    rows.forEach((row: any[], rowIndex: number) => {
      const rowObj: any = {};
      if (includeCheckboxes) {
        rowObj.rowSelection = '';
        
        // Add match value from work data for source grids
        if (!isTargetGrid && currentData && currentData.rows && currentData.rows[rowIndex]) {
          rowObj._match = currentData.rows[rowIndex].match;
        }
      }
      headers.forEach((header: string, index: number) => {
        rowObj[header] = row[index];
      });
      rowData.push(rowObj);
    });
    
    return { columnDefs, rowData };
  }
  
  // updateSourceDataFromGrid and updateSourceDataFromGridSilently removed
  // SourceTableEditor manages its own grid data updates

  function updateWorkingDataFromGrid(params: any) {
    if (!workingData || !params) return;
    
    // Set flag to prevent reactive updates
    isUpdatingCell = true;
    
    // Use params.data[colDef.field] to get the value after valueSetter conversion (handles lookup ID conversion)
    const { rowIndex, colDef } = params;
    const cellValue = params.data[colDef.field]; // This has the ID after valueSetter conversion
    
    const headers = sourceData2D[0];
    const colIndex = headers.indexOf(colDef.field);
    
    if (colIndex >= 0 && rowIndex >= 0) {
      // Find the corresponding row and column in working data
      const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
      if (currentData && (currentData as any).rows && (currentData as any).columns && (currentData as any).rows[rowIndex] && (currentData as any).columns[colIndex]) {
        const rowId = (currentData as any).rows[rowIndex].id;
        const colId = (currentData as any).columns[colIndex].id;
        
        updateCellValue(workingData, currentTableIndex, rowId, colId, cellValue, "user", effectiveTableId);
        saveWorkingData();
      }
    }
    
    // Clear flag after a brief delay
    setTimeout(() => {
      isUpdatingCell = false;
    }, 100);
  }
  
  function updateTargetGrid() {
    // Determine which grid to update based on current mode
    const isDocumentMode = currentMode === 'source-document';
    const gridApi = isDocumentMode ? documentTargetGridApi : studyTargetGridApi;
    const targetData = isDocumentMode ? documentTargetData2D : studyTargetData2D;
    
    if (!gridApi || targetData.length === 0) return;
    
    const { columnDefs, rowData } = convertToGridFormat(targetData, true, true);
    (gridApi as any).updateGridOptions({ columnDefs: columnDefs as any, rowData } as any);
    
    // Update the store after grid update
    if (isDocumentMode) {
      updateDocumentExtractStore(documentTargetData2D);
    } else {
      updateStudyExtractStore(studyTargetData2D);
    }
  }
  
  function initializeDocumentTargetGrid() {
    if (!documentTargetGridDiv || documentTargetData2D.length === 0) {
      return;
    }
    
    // Destroy existing grid
    if (documentTargetGridInstance) {
      documentTargetGridInstance.destroy();
      documentTargetGridInstance = null;
      documentTargetGridApi = null;
    }
    
    const { columnDefs, rowData } = convertToGridFormat(documentTargetData2D, true, true);
    const gridOptions = {
      columnDefs: columnDefs as any,
      rowData,
      domLayout: 'normal' as const,
      suppressCellFocus: false, 
      rowHeight: 32,
      headerHeight: 40,
      autoSizeStrategy: {
        type: 'fitCellContents',
      },
      suppressColumnVirtualisation: true,
      suppressRowVirtualisation: true,
      suppressContextMenu: true,
      suppressMovableColumns: true, 
      rowSelection: {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: true,
        enableClickSelection: true
      },
      // Register custom header component for target columns
      components: {
        columnHeaderForTarget: ColumnHeaderForTarget
      },
      defaultColDef: {
        editable: true,
        resizable: false,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true
      },
      onGridReady: (params: any) => {
        documentTargetGridApi = params.api;
      },
      onSelectionChanged: (params: any) => {
        const selectedNodes = params.api.getSelectedNodes();
        const newSelectedRows = new Set<number>();
        
        selectedNodes.forEach((node: any) => {
          if (node.rowIndex !== null && node.rowIndex !== undefined) {
            newSelectedRows.add(node.rowIndex);
          }
        });
        
        selectedDocumentTargetRows = newSelectedRows;
      },
      onCellValueChanged: (params: any) => {
        // References don't use groupers - all columns are editable
        // Use params.data[colDef.field] to get the value after valueSetter conversion (handles lookup ID conversion)
        const { rowIndex, colDef } = params;
        const cellValue = params.data[colDef.field]; // This has the ID after valueSetter conversion
        
        const headers = documentTargetData2D[0];
        const colIndex = headers.indexOf(colDef.field);
        
        if (colIndex >= 0 && rowIndex >= 0) {
          const actualRowIndex = rowIndex + 1; // Account for header row
          if (actualRowIndex < documentTargetData2D.length) {
            documentTargetData2D[actualRowIndex][colIndex] = cellValue;
            
            // Update the store
            updateDocumentExtractStore(documentTargetData2D);
            
          }
        }
      }
    };
    
    documentTargetGridInstance = createGrid(documentTargetGridDiv, gridOptions as any);
    documentTargetGridInitialized = true; // Mark document target grid as initialized
  }
  
  function initializeStudyTargetGrid() {
    if (!studyTargetGridDiv || studyTargetData2D.length === 0) {
      return;
    }
    
    // Destroy existing grid
    if (studyTargetGridInstance) {
      studyTargetGridInstance.destroy();
      studyTargetGridInstance = null;
      studyTargetGridApi = null;
    }
    
    const { columnDefs, rowData } = convertToGridFormat(studyTargetData2D, true, true);
    const gridOptions = {
      columnDefs: columnDefs as any,
      rowData,
      domLayout: 'normal' as const,
      suppressCellFocus: false, // Enable cell editing
      rowHeight: 32,
      headerHeight: 40, // Set to 40px for target grid
      autoSizeStrategy: {
        type: 'fitCellContents',
      },
      suppressColumnVirtualisation: true,
      suppressRowVirtualisation: true,
      suppressContextMenu: true, // Suppress right-click context menu
      suppressMovableColumns: true, // Prevent column reordering in target grid
      // AG-Grid built-in row selection for study target grid
      rowSelection: {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: true,
        enableClickSelection: true
      },
      // Register custom header component for target columns
      components: {
        columnHeaderForTarget: ColumnHeaderForTarget
      },
      defaultColDef: {
        editable: true, // Enable cell editing
        resizable: false,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true
      },
      onGridReady: (params: any) => {
        studyTargetGridApi = params.api;
      },
      onSelectionChanged: (params: any) => {
        const selectedNodes = params.api.getSelectedNodes();
        const newSelectedRows = new Set<number>();
        
        selectedNodes.forEach((node: any) => {
          if (node.rowIndex !== null && node.rowIndex !== undefined) {
            newSelectedRows.add(node.rowIndex);
          }
        });
        
        selectedStudyTargetRows = newSelectedRows;
      },
      onCellValueChanged: (params: any) => {
        // References don't use groupers - all columns are editable
        // Use params.data[colDef.field] to get the value after valueSetter conversion (handles lookup ID conversion)
        const { rowIndex, colDef } = params;
        const cellValue = params.data[colDef.field]; // This has the ID after valueSetter conversion
        
        const headers = studyTargetData2D[0];
        const colIndex = headers.indexOf(colDef.field);
        
        if (colIndex >= 0 && rowIndex >= 0) {
          const actualRowIndex = rowIndex + 1; // Account for header row
          if (actualRowIndex < studyTargetData2D.length) {
            studyTargetData2D[actualRowIndex][colIndex] = cellValue;
            
            // Update the store
            updateStudyExtractStore(studyTargetData2D);
            
          }
        }
      }
    };
    
    studyTargetGridInstance = createGrid(studyTargetGridDiv, gridOptions as any);
    studyTargetGridInitialized = true; // Mark study target grid as initialized
  }
  
  function updateStudyTargetGrid() {
    if (!studyTargetGridApi || studyTargetData2D.length === 0) return;
    
    const { columnDefs, rowData } = convertToGridFormat(studyTargetData2D, true, true);
    (studyTargetGridApi as any).updateGridOptions({ columnDefs: columnDefs as any, rowData } as any);
  }
  
  function handleSaveAndClose() {
    if (!$studyExtractStore || !$projectStructureStore) return;
    
    try {
      // Convert target 2D array back to study extract format
      // For source-study mode, use studyTargetData2D; for source-document mode, this shouldn't be called
      const targetData = currentMode === 'source-study' ? studyTargetData2D : 
                        currentMode === 'source-document' ? documentTargetData2D :
                        studyTargetData2D; // default to study for document-study mode
      
      const updatedTableData = arrayToStudyTable(
        targetData,
        topicId,
        effectiveTableId,
        $projectStructureStore,
        $studyExtractStore
      );
      
      // Update study extract
      const study = JSON.parse(JSON.stringify($studyExtractStore));
      const topic = study.topics.find((t: any) => t.id === topicId);
      
      if (topic) {
        const table = topic.tables.find((t: any) => t.id === effectiveTableId);
        if (table) {
          table.rows = updatedTableData.rows;
          
          // Update store
          studyExtractStore.set(study);
        }
      }
      
      show = false;
    } catch (error) {
      logError(error, "ReferenceTableEditor", undefined, { message: "Error saving data" });
      alert('Error saving data. Please check console for details.');
    }
  }
  
  function handleCancel() {
    show = false;
    sourceGridInitialized = false; // Reset source grid initialization flag
    documentTargetGridInitialized = false; // Reset document target grid initialization flag
    studyTargetGridInitialized = false; // Reset study target grid initialization flag
  }

  // Column popup functions moved to SourceTableEditor
  // Callback handlers for SourceTableEditor
  
  function handleSaveColumn(columnName: string, columnKey: string, mode: 'add' | 'edit', columnIndex: number) {
    if (mode === 'add') {
      // Add new column using next_col
      if (workingData && currentTableIndex >= 0) {
        const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
        if (currentData && (currentData as any).next_col) {
          // Use the next_col value as the new column ID
          const newColumnId = (currentData as any).next_col;
          
          // Increment next_col for future additions
          const nextColNumber = parseInt((currentData as any).next_col.replace('c', '')) + 1;
          (currentData as any).next_col = `c${nextColNumber}`;
          
          // Add the new column
          addColumn(workingData, currentTableIndex, columnName, columnKey, effectiveTableId);
          
          // Set match status for the newly added column
          const newColumnIndex = (currentData as any).columns.length - 1;
          if ((currentData as any).columns[newColumnIndex]) {
            (currentData as any).columns[newColumnIndex].match = !!columnKey && columnKey.trim() !== '';
          }
          
          saveWorkingData();
          handleRefreshSourceData();
        }
      }
    } else if (mode === 'edit' && columnIndex >= 0) {
      // Edit existing column
      if (workingData && currentTableIndex >= 0) {
        const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
        if (currentData && (currentData as any).columns && (currentData as any).columns[columnIndex]) {
          // Update working data first
          const column = (currentData as any).columns[columnIndex];
          column.label = columnName;
          column.key = columnKey;
          // Set match based on whether key is assigned
          column.match = !!columnKey && columnKey.trim() !== '';
          saveWorkingData();
          
          // Refresh sourceData2D to ensure it's in sync with workingData
          handleRefreshSourceData();
          
          // Run validation after a brief delay to ensure sourceData2D is updated
          setTimeout(() => {
            runValidation();
          }, 50);
        }
      }
    }
  }
  
  function handleRemoveColumn(columnIndex: number) {
    if (workingData && currentTableIndex >= 0) {
      const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
      if (currentData) {
        const colId = (currentData as any).columns && (currentData as any).columns[columnIndex] ? (currentData as any).columns[columnIndex].id : null;
        if (colId) {
          removeColumn(workingData, currentTableIndex, colId, effectiveTableId);
          saveWorkingData();
          handleRefreshSourceData();
          selectedColumns.clear();
        }
      }
    }
  }

  function updateDocumentExtractStore(data2D: any[][]) {
    try {
      // Create a deep copy of the store
      const updatedStore = JSON.parse(JSON.stringify($documentExtractFullStore));
      
      if (isReferenceMode) {
        // Reference mode
        if (!$projectStructureStore || !referenceId) return;
        
        // Convert 2D array back to reference format
        const referenceData = arrayToReference(data2D, referenceId, $projectStructureStore, updatedStore);
        
        // Find and update the reference
        const reference = updatedStore.references?.find((r: any) => r.id === referenceId);
        if (reference) {
          reference.rows = referenceData.rows;
        } else {
          // Create new reference if it doesn't exist
          if (!updatedStore.references) {
            updatedStore.references = [];
          }
          updatedStore.references.push({
            id: referenceId,
            rows: referenceData.rows
          });
        }
      } else {
        // Topic mode (existing logic)
        if (!$projectStructureStore || !topicId || !effectiveTableId) return;
        
        // Find the topic
        const topic = updatedStore.topics?.find((t: any) => t.id === topicId);
        if (!topic) return;
        
        // Get table structure for column IDs
        const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
        
        // Find the table (grouper editing is handled by separate GrouperEditor component)
        const table = topic.tables?.find((t: any) => t.id === effectiveTableId);
        if (!table) return;
        
        // Get table structure for column IDs
        const tableStructure = topicStructure?.tables?.find((t: any) => t.id === effectiveTableId);
        if (!tableStructure) return;
        
        // Convert 2D array back to TARGET table format (preserves row IDs)
        const tableData = convert2DArrayToTargetTable(data2D, tableStructure, table);
        table.rows = tableData.rows;
      }
      
      // Update the store
      documentExtractFullStore.set(updatedStore);
      
    } catch (error) {
      logError(error, "ReferenceTableEditor", undefined, { message: "Error updating documentExtractFullStore" });
    }
  }
  
  function updateStudyExtractStore(data2D: any[][]) {
    try {
      // Create a deep copy of the store
      const updatedStore = JSON.parse(JSON.stringify($studyExtractStore));
      
      if (isReferenceMode) {
        // Reference mode
        if (!$projectStructureStore || !referenceId) return;
        
        // Convert 2D array back to reference format
        const referenceData = arrayToReference(data2D, referenceId, $projectStructureStore, updatedStore);
        
        // Find and update the reference
        const reference = updatedStore.references?.find((r: any) => r.id === referenceId);
        if (reference) {
          reference.rows = referenceData.rows;
        } else {
          // Create new reference if it doesn't exist
          if (!updatedStore.references) {
            updatedStore.references = [];
          }
          updatedStore.references.push({
            id: referenceId,
            rows: referenceData.rows
          });
        }
      } else {
        // Topic mode (existing logic)
        if (!$projectStructureStore || !topicId || !effectiveTableId) return;
        
        // Find the topic
        const topic = updatedStore.topics?.find((t: any) => t.id === topicId);
        if (!topic) return;
        
        // Find the table
        const table = topic.tables?.find((t: any) => t.id === effectiveTableId);
        if (!table) return;
        
        // Get table structure for column IDs
        const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
        const tableStructure = topicStructure?.tables?.find((t: any) => t.id === effectiveTableId);
        if (!tableStructure) return;
        
        // Convert 2D array back to TARGET table format (preserves row IDs)
        const tableData = convert2DArrayToTargetTable(data2D, tableStructure, table);
        table.rows = tableData.rows;
      }
      
      // Update the store
      studyExtractStore.set(updatedStore);
    } catch (error) {
      logError(error, "ReferenceTableEditor", undefined, { message: "Error updating studyExtractStore" });
    }
  }
  
  /**
   * Convert 2D array to TARGET table format (document/study extract)
   * Preserves original row IDs like "row_study", "row_arm_1", etc.
   * Generates "r_new1", "r_new2" for new rows
   */
  function convert2DArrayToTargetTable(data2D: any[][], tableStructure: any, originalTableData?: any): any {
    if (data2D.length < 2) return { rows: [] };
    
    const headers = data2D[0];
    const rows = data2D.slice(1);
    
    // Find the highest existing r_new{N} number
    let maxNewRowNum = 0;
    if (originalTableData?.rows) {
      originalTableData.rows.forEach((row: any) => {
        if (row.id.startsWith('r_new')) {
          const num = parseInt(row.id.substring(5));
          if (!isNaN(num) && num > maxNewRowNum) {
            maxNewRowNum = num;
          }
        }
      });
    }
    let newRowCounter = maxNewRowNum;
    
    return {
      rows: rows.map((row, index) => {
        const cells: any[] = [];
        
        headers.forEach((header, colIndex) => {
          // Find the column in the table structure that matches this header label
          const column = tableStructure.columns?.find((col: any) => col.label === header);
          const columnId = column?.id || header.toLowerCase().replace(/\s+/g, '_').replace(/[?!]/g, '');
          
          // Create cell object matching the expected structure (cells array with id and value)
          cells.push({
            id: columnId,
            value: row[colIndex] || ''
          });
        });
        
        // Preserve existing row ID if available, otherwise generate new ID
        const existingRow = originalTableData?.rows?.[index];
        let rowId;
        if (existingRow?.id) {
          rowId = existingRow.id;
        } else {
          // New row - generate r_new{N}
          newRowCounter++;
          rowId = `r_new${newRowCounter}`;
        }
        
        return {
          id: rowId,
          cells: cells
        };
      })
    };
  }
  
  /**
   * Convert 2D array to SOURCE table format (working data)
   * Always generates sequential IDs: r1, r2, r3, etc.
   */
  function convert2DArrayToSourceTable(data2D: any[][]): any {
    if (data2D.length < 2) return { rows: [] };
    
    const headers = data2D[0];
    const rows = data2D.slice(1);
    
    return {
      rows: rows.map((row, index) => {
        const rowObj: any = { id: `r${index + 1}` };
        headers.forEach((header, colIndex) => {
          const key = header.toLowerCase().replace(/\s+/g, '_').replace(/[?!]/g, '');
          rowObj[key] = row[colIndex] || '';
        });
        return rowObj;
      })
    };
  }
  
  // Grouper editing functions removed - use GrouperEditor component instead
  // Functions removed:
  // - convertGrouperToEditorGridFormat
  // - getSourceOptions (for grouper)
  // - findRefIDByDisplayName (for grouper)
  // - handleGrouperCellChange

  function handleTargetAddRow() {
    
    // Determine which grid and data to use based on current mode
    const isDocumentMode = currentMode === 'source-document';
    
    // Grouper editing is handled by separate GrouperEditor component
    
    const targetData = isDocumentMode ? documentTargetData2D : studyTargetData2D;
    const gridApi = isDocumentMode ? documentTargetGridApi : studyTargetGridApi;
    
    // Set the appropriate flag based on current mode
    if (isDocumentMode) {
      isUpdatingDocumentTarget = true;
    } else {
      isUpdatingStudyTarget = true;
    }
    
    if (targetData.length > 0) {
      // Use consolidated function from target-table-processing
      const updatedData = addTargetRow(targetData);
      
      // Update the appropriate targetData and store
      if (isDocumentMode) {
        documentTargetData2D = updatedData;
        updateDocumentExtractStore(documentTargetData2D);
        // Save to localStorage
        if ($documentExtractFullStore) {
          saveTargetExtract('document', 'document_1', $documentExtractFullStore);
        }
      } else {
        studyTargetData2D = updatedData;
        updateStudyExtractStore(studyTargetData2D);
        // Save to localStorage
        if ($studyExtractStore) {
          saveTargetExtract('study', 'study_1', $studyExtractStore);
        }
      }
      
      // Refresh the grid to show the new row
      if (gridApi) {
        const { columnDefs, rowData } = convertToGridFormat(updatedData, true, true);
        gridApi.setGridOption('rowData', rowData);
      }
    }
    
    // Clear the appropriate flag based on current mode
    if (isDocumentMode) {
      isUpdatingDocumentTarget = false;
      documentTargetGridInitialized = true;
    } else {
      isUpdatingStudyTarget = false;
      studyTargetGridInitialized = true;
    }
  }

  function handleTargetRemoveRow() {
    // Determine which grid and data to use based on current mode
    const isDocumentMode = currentMode === 'source-document';
    const selectedRows = isDocumentMode ? selectedDocumentTargetRows : selectedStudyTargetRows;
    const targetData = isDocumentMode ? documentTargetData2D : studyTargetData2D;
    const gridApi = isDocumentMode ? documentTargetGridApi : studyTargetGridApi;
    
    
    // Set the appropriate flag based on current mode
    if (isDocumentMode) {
      isUpdatingDocumentTarget = true;
    } else {
      isUpdatingStudyTarget = true;
    }
    
    if (selectedRows.size > 0) {
      // Use consolidated function from target-table-processing
      const rowIndices = Array.from(selectedRows);
      const updatedData = removeTargetRows(targetData, rowIndices);
      
      // Update the appropriate targetData and store
      if (isDocumentMode) {
        documentTargetData2D = updatedData;
        updateDocumentExtractStore(documentTargetData2D);
        // Save to localStorage
        if ($documentExtractFullStore) {
          saveTargetExtract('document', 'document_1', $documentExtractFullStore);
        }
      } else {
        studyTargetData2D = updatedData;
        updateStudyExtractStore(studyTargetData2D);
        // Save to localStorage
        if ($studyExtractStore) {
          saveTargetExtract('study', 'study_1', $studyExtractStore);
        }
      }
      
      // Refresh the grid to show the updated data
      if (gridApi) {
        const { columnDefs, rowData } = convertToGridFormat(updatedData, true, true);
        gridApi.setGridOption('rowData', rowData);
      }
      
      // Clear selection
      if (isDocumentMode) {
        selectedDocumentTargetRows.clear();
      } else {
        selectedStudyTargetRows.clear();
      }
      
    }
    
    // Clear the appropriate flag based on current mode
    if (isDocumentMode) {
      isUpdatingDocumentTarget = false;
      documentTargetGridInitialized = true;
    } else {
      isUpdatingStudyTarget = false;
      studyTargetGridInitialized = true;
    }
  }

  function handleDocumentTargetTableReset() {
    // Reload document target data
    const context: DataLoadingContext = {
      documentExtractStore: $documentExtractStore,
      documentExtractFullStore: $documentExtractFullStore,
      studyExtractStore: $studyExtractStore,
      projectStructureStore: $projectStructureStore,
      topicId,
      tableId
    };
    
    const result = loadDataForModeExternal('document-study', context);
    const { documentData } = result as { documentData: any[][], studyData: any[][] };
    documentTargetData2D = documentData;
    
    if (documentTargetGridApi) {
      const { columnDefs, rowData } = convertToGridFormat(documentTargetData2D, true, true);
      documentTargetGridApi.setGridOption('rowData', rowData);
    }
  }

  function handleStudyTargetTableReset() {
    // Reload study target data
    const context: DataLoadingContext = {
      documentExtractStore: $documentExtractStore,
      documentExtractFullStore: $documentExtractFullStore,
      studyExtractStore: $studyExtractStore,
      projectStructureStore: $projectStructureStore,
      topicId,
      tableId
    };
    
    const result = loadDataForModeExternal('document-study', context);
    const { studyData } = result as { documentData: any[][], studyData: any[][] };
    studyTargetData2D = studyData;
    
    if (studyTargetGridApi) {
      const { columnDefs, rowData } = convertToGridFormat(studyTargetData2D, true, true);
      studyTargetGridApi.setGridOption('rowData', rowData);
    }
  }

  function handleTargetTableReset() {
    // Reset target table to original state
    // This would need to reload the original target data from the appropriate source
    const context: DataLoadingContext = {
      documentExtractStore: $documentExtractStore,
      documentExtractFullStore: $documentExtractFullStore,
      studyExtractStore: $studyExtractStore,
      projectStructureStore: $projectStructureStore,
      topicId,
      tableId
    };
    
    if (currentMode === 'source-document') {
      // Reload from document extract
      documentTargetData2D = loadDataForModeExternal(currentMode, context) as any[][];
      updateTargetGrid();
    } else if (currentMode === 'source-study') {
      // Reload from study extract
      studyTargetData2D = loadDataForModeExternal(currentMode, context) as any[][];
      updateTargetGrid();
    }
  }

  // Source grid operations moved to SourceTableEditor
  // Callback handlers for SourceTableEditor
  
  // No-op functions for SourceTableEditor callbacks (handled internally)
  const noop = () => {};
  const noopWithEvent = (e?: MouseEvent) => {};
  
  function handleRefreshSourceData() {
    // Refresh sourceData2D from workingData
    if (workingData && currentTableIndex >= 0) {
      const workingTable = workingData.tables[currentTableIndex];
      const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
      if (currentData && workingTable) {
        sourceData2D = workingTableTo2DArray(currentData);
        originalSourceData = JSON.parse(JSON.stringify(sourceData2D));
        hasChanges = false;
        
        // SourceTableEditor will automatically refresh the grid via its reactive statement
        // when sourceData2D changes
        
        runValidation();
      }
    }
  }

  function handleMatchOnly() {
    if (!hasColumnMatch) {
      alert('No columns match between source and target. Cannot perform replace operation.');
      return;
    }
    
    // References don't use grouperColumns - matching uses all matching keys
    // The matchOnlySyncWithKeys function will use all matching keys when grouperColumns is empty
    
    // Build column key mappings
    const sourceKeyMapping = buildSourceKeyMapping(workingData, currentTableIndex, effectiveTableId, sourceData2D[0]);
    const targetHeaders = currentMode === 'source-document' ? documentTargetData2D[0] : studyTargetData2D[0];
    const targetKeyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, targetHeaders, referenceId);
    
    // Determine which target data to update based on current mode
    // References don't use grouperColumns - use empty array (matching uses all matching keys)
    const grouperColumns: string[] = []; // References don't have grouper columns
    if (currentMode === 'source-document') {
      documentTargetData2D = matchOnlySyncWithKeys(sourceData2D, documentTargetData2D, grouperColumns, sourceKeyMapping, targetKeyMapping);
      updateDocumentExtractStore(documentTargetData2D);
    } else if (currentMode === 'source-study') {
      studyTargetData2D = matchOnlySyncWithKeys(sourceData2D, studyTargetData2D, grouperColumns, sourceKeyMapping, targetKeyMapping);
      updateStudyExtractStore(studyTargetData2D);
    }
    updateTargetGrid();
  }
  
  function handleMatchAll() {
    if (!hasColumnMatch) {
      alert('No columns match between source and target. Cannot perform replace operation.');
      return;
    }
    
    // Build column key mappings
    const sourceKeyMapping = buildSourceKeyMapping(workingData, currentTableIndex, effectiveTableId, sourceData2D[0]);
    const targetHeaders = currentMode === 'source-document' ? documentTargetData2D[0] : studyTargetData2D[0];
    const targetKeyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, targetHeaders, referenceId);
    
    // Determine which target data to update based on current mode
    if (currentMode === 'source-document') {
      documentTargetData2D = matchAllSyncWithKeys(sourceData2D, documentTargetData2D, sourceKeyMapping, targetKeyMapping);
      updateDocumentExtractStore(documentTargetData2D);
    } else if (currentMode === 'source-study') {
      studyTargetData2D = matchAllSyncWithKeys(sourceData2D, studyTargetData2D, sourceKeyMapping, targetKeyMapping);
      updateStudyExtractStore(studyTargetData2D);
    }
    updateTargetGrid();
  }
  

  function handleDetectMappings() {
    if (validationResult) {
      const summary = getValidationSummary(validationResult);
      alert(`Validation Results:\n\n${summary}\n\nSync Preview:\n${syncSummary}`);
    }
  }
  
  // Grid initialization is handled in the individual reactive statements above
  
  // Mode change handler
  function handleModeChange(mode: FlowMode) {
    currentMode = mode;
    
    // Clear selection state when switching modes
    selectedRows.clear();
    selectedColumns.clear();
    selectedDocumentTargetRows.clear();
    selectedStudyTargetRows.clear();
    
    // Reset flags and grids when mode changes
    sourceGridInitialized = false; // Reset source grid initialization flag
    documentTargetGridInitialized = false; // Reset document target grid initialization flag
    studyTargetGridInitialized = false; // Reset study target grid initialization flag
    resetGrids();
    loadDataForMode();
  }
  
  function resetGrids() {
    // Destroy all existing grids (source grid is handled by SourceTableEditor)
    sourceGridApi = null; // SourceTableEditor will handle its own grid destruction
    if (documentTargetGridInstance) {
      documentTargetGridInstance.destroy();
      documentTargetGridInstance = null;
      documentTargetGridApi = null;
    }
    if (studyTargetGridInstance) {
      studyTargetGridInstance.destroy();
      studyTargetGridInstance = null;
      studyTargetGridApi = null;
    }
    
    sourceGridInitialized = false; // SourceTableEditor manages this
    documentTargetGridInitialized = false;
    studyTargetGridInitialized = false;
  }
  
  function loadDataForMode() {
    const context: DataLoadingContext = {
      documentExtractStore: $documentExtractStore,
      documentExtractFullStore: $documentExtractFullStore,
      studyExtractStore: $studyExtractStore,
      projectStructureStore: $projectStructureStore,
      topicId: isReferenceMode ? '' : topicId,
      tableId: isReferenceMode ? '' : effectiveTableId,
      referenceId: isReferenceMode ? referenceId : undefined
    };
    
    const result = loadDataForModeExternal(currentMode, context);
    
    switch (currentMode) {
      case 'source-document':
        documentTargetData2D = result as any[][];
        // Grid initialization will be handled by reactive statement
        break;
      case 'source-study':
        studyTargetData2D = result as any[][];
        // Grid initialization will be handled by reactive statement
        break;
      case 'document-study':
        const { documentData, studyData } = result as { documentData: any[][], studyData: any[][] };
        documentTargetData2D = documentData;
        studyTargetData2D = studyData;
        // Grid initialization will be handled by reactive statements
        break;
    }
  }
  
  // New handler functions for the additional buttons
  function handleMatchSelected() {
    if (!hasColumnMatch) {
      alert('No columns match between source and target. Cannot perform replace operation.');
      return;
    }
    
    const selectedRows = getSelectedRowsFromGrid(sourceGridApi);
    
    if (selectedRows.size === 0) {
      alert('Please select rows to replace');
      return;
    }
    
    // Filter source data to only include selected rows
    const filteredSourceData = filterRowsBySelection(sourceData2D, selectedRows);
    
    if (filteredSourceData.length <= 1) {
      alert('No valid source rows selected');
      return;
    }
    
    // Build column key mappings
    const sourceKeyMapping = buildSourceKeyMapping(workingData, currentTableIndex, effectiveTableId, filteredSourceData[0]);
    const targetHeaders = currentMode === 'source-document' ? documentTargetData2D[0] : studyTargetData2D[0];
    const targetKeyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, targetHeaders, referenceId);
    
    // Determine which target data to update based on current mode
    // References don't use grouperColumns - use empty array (matching uses all matching keys)
    const grouperColumns: string[] = []; // References don't have grouper columns
    if (currentMode === 'source-document') {
      documentTargetData2D = matchSelectedSyncWithKeys(filteredSourceData, documentTargetData2D, grouperColumns, sourceKeyMapping, targetKeyMapping);
      updateDocumentExtractStore(documentTargetData2D);
    } else if (currentMode === 'source-study') {
      studyTargetData2D = matchSelectedSyncWithKeys(filteredSourceData, studyTargetData2D, grouperColumns, sourceKeyMapping, targetKeyMapping);
      updateStudyExtractStore(studyTargetData2D);
    }
    updateTargetGrid();
  }
  
  function handleDocumentTargetAddRow() {
    
    // Grouper editing is handled by separate GrouperEditor component
    
    isUpdatingDocumentTarget = true; // Prevent reactive statement from interfering
    
    if (documentTargetData2D.length > 0) {
      // Use consolidated function from target-table-processing
      const updatedData = addTargetRow(documentTargetData2D);
      documentTargetData2D = updatedData;
      
      // Update the document extract store
      updateDocumentExtractStore(documentTargetData2D);
      
      // Save to localStorage
      if ($documentExtractFullStore) {
        saveTargetExtract('document', 'document_1', $documentExtractFullStore);
      }
      
      // Refresh the document target grid to show the new row
      if (documentTargetGridApi) {
        const { columnDefs, rowData } = convertToGridFormat(documentTargetData2D, true, true);
        documentTargetGridApi.setGridOption('rowData', rowData);
      }
    }
    
    isUpdatingDocumentTarget = false; // Re-enable reactive statements
    documentTargetGridInitialized = true; // Mark document target grid as initialized to prevent reactive statement from running
  }
  
  function handleStudyTargetAddRow() {
    isUpdatingStudyTarget = true; // Prevent reactive statement from interfering
    
    if (studyTargetData2D.length > 0 && $studyExtractStore && referenceId && $projectStructureStore) {
      // Use consolidated function from target-table-processing to add row to 2D array
      const updatedData = addTargetRow(studyTargetData2D);
      studyTargetData2D = updatedData;
      
      // Create a deep copy of the store and update it
      const updatedStore = JSON.parse(JSON.stringify($studyExtractStore));
      
      // Convert 2D array back to reference format
      const referenceData = arrayToReference(updatedData, referenceId, $projectStructureStore, updatedStore);
      
      // Find and update the reference
      const reference = updatedStore.references?.find((r: any) => r.id === referenceId);
      if (reference) {
        reference.rows = referenceData.rows;
      } else {
        // Create new reference if it doesn't exist
        if (!updatedStore.references) {
          updatedStore.references = [];
        }
        updatedStore.references.push({
          id: referenceId,
          rows: referenceData.rows
        });
      }
      
      // Update the store (this should trigger ExtractItems2 to refresh)
      studyExtractStore.set(updatedStore);
      
      // Save to localStorage
      saveTargetExtract('study', 'study_1', updatedStore);
      
      // Refresh studyTargetData2D from updated store to get the new row ID
      studyTargetData2D = referenceTo2DArray(referenceId, updatedStore, $projectStructureStore);
      
      // Manually refresh the grid after adding row using the updated reference data
      if (studyTargetGridApi) {
        if (reference && reference.rows && referenceColumnStructure && referenceColumnStructure.length > 0) {
          // Convert rows to AG Grid format and update
          const rowData = convertReferenceRowsToGridData(reference.rows, referenceColumnStructure);
          studyTargetGridApi.setGridOption('rowData', rowData);
        } else {
          logWarning('Cannot refresh grid - missing data:', "ReferenceTableEditor", undefined, {
            hasReference: !!reference,
            hasRows: !!(reference && reference.rows),
            rowsCount: reference?.rows?.length || 0,
            hasColumnStructure: !!referenceColumnStructure,
            columnStructureLength: referenceColumnStructure?.length || 0
          });
        }
      } else {
        logWarning('Cannot refresh grid - studyTargetGridApi is not available', "ReferenceTableEditor", undefined);
      }
      
    }
    
    isUpdatingStudyTarget = false; // Re-enable reactive statements
    studyTargetGridInitialized = true; // Mark study target grid as initialized to prevent reactive statement from running
  }
  
  // Helper function to convert reference rows to grid data format
  function convertReferenceRowsToGridData(rows: any[], columnStructure: any[]): any[] {
    return rows.map(row => {
      const rowObj: any = { id: row.id };
      columnStructure.forEach(column => {
        const cell = row.cells?.find((c: any) => c.id === column.id);
        rowObj[column.id] = cell?.value || '-';
        if (cell?.sourceInfo) {
          rowObj[`${column.id}_sourceInfo`] = cell.sourceInfo;
        }
      });
      return rowObj;
    });
  }

  function handleDocumentTargetRemoveRow() {
    isUpdatingDocumentTarget = true; // Prevent reactive statement from interfering
    
    if (selectedDocumentTargetRows.size > 0) {
      // Use consolidated function from target-table-processing
      const rowIndices = Array.from(selectedDocumentTargetRows);
      const updatedData = removeTargetRows(documentTargetData2D, rowIndices);
      documentTargetData2D = updatedData;
      
      // Update the document extract store
      updateDocumentExtractStore(documentTargetData2D);
      
      // Save to localStorage
      if ($documentExtractFullStore) {
        saveTargetExtract('document', 'document_1', $documentExtractFullStore);
      }
      
      // Refresh the document target grid to show the updated data
      if (documentTargetGridApi) {
        const { columnDefs, rowData } = convertToGridFormat(documentTargetData2D, true, true);
        documentTargetGridApi.setGridOption('rowData', rowData);
      }
      
      // Clear selection
      selectedDocumentTargetRows.clear();
      
    }
    
    isUpdatingDocumentTarget = false; // Re-enable reactive statements
    documentTargetGridInitialized = true; // Mark document target grid as initialized to prevent reactive statement from running
  }
  
  function handleStudyTargetRemoveRow() {
    isUpdatingStudyTarget = true; // Prevent reactive statement from interfering
    
    if (selectedStudyTargetRows.size > 0 && studyTargetGridApi && $studyExtractStore && referenceId && $projectStructureStore) {
      // Get selected nodes from grid
      const selectedNodes = studyTargetGridApi.getSelectedNodes();
      const rowIdsToRemove = selectedNodes.map((node: any) => node.data?.id).filter((id: any) => id);
      
      if (rowIdsToRemove.length > 0) {
        // Remove rows from store
        const updatedStudy = JSON.parse(JSON.stringify($studyExtractStore));
        const reference = updatedStudy.references?.find((r: any) => r.id === referenceId);
        if (reference && reference.rows) {
          // Filter out the rows to remove
          reference.rows = reference.rows.filter((row: any) => !rowIdsToRemove.includes(row.id));
          
          // Update the store
          studyExtractStore.set(updatedStudy);
          
          // Save to localStorage
          saveTargetExtract('study', 'study_1', updatedStudy);
          
          // Refresh studyTargetData2D from updated store
          studyTargetData2D = referenceTo2DArray(referenceId, updatedStudy, $projectStructureStore);
          
          // Manually refresh the grid after removing rows using the updated reference data
          if (studyTargetGridApi) {
            if (reference.rows && referenceColumnStructure && referenceColumnStructure.length > 0) {
              // Convert rows to AG Grid format and update
              const rowData = convertReferenceRowsToGridData(reference.rows, referenceColumnStructure);
              studyTargetGridApi.setGridOption('rowData', rowData);
            } else {
              logWarning('Cannot refresh grid after remove - missing data:', "ReferenceTableEditor", undefined, {
                hasRows: !!reference.rows,
                rowsCount: reference.rows?.length || 0,
                hasColumnStructure: !!referenceColumnStructure,
                columnStructureLength: referenceColumnStructure?.length || 0
              });
            }
          } else {
            logWarning('Cannot refresh grid after remove - studyTargetGridApi is not available', "ReferenceTableEditor", undefined);
          }
        }
      }
      
      // Clear selection
      selectedStudyTargetRows.clear();
      if (studyTargetGridApi) {
        studyTargetGridApi.deselectAll();
      }
      
    }
    
    isUpdatingStudyTarget = false; // Re-enable reactive statements
    studyTargetGridInitialized = true; // Mark study target grid as initialized to prevent reactive statement from running
  }
  
  // Handler for ReferenceGrid cell changes
  function handleStudyTargetCellValueChanged(params: any) {
    // ReferenceGrid's default handler already updates the store
    // We just need to refresh studyTargetData2D from the updated store
    if ($studyExtractStore && referenceId && $projectStructureStore) {
      studyTargetData2D = referenceTo2DArray(referenceId, $studyExtractStore, $projectStructureStore);
    }
  }
  
  // Handler for ReferenceGrid selection changes
  function handleStudyTargetSelectionChanged(selectedRows: Set<number>) {
    selectedStudyTargetRows = selectedRows;
  }
  
  // Handler for ReferenceGrid grid ready
  function handleStudyTargetGridReady(api: GridApi) {
    studyTargetGridApi = api;
    studyTargetGridInitialized = true;
  }
  
  function handleDocumentStudySelected() {
    const selectedRows = getSelectedRowsFromGrid(documentTargetGridApi);
    
    if (selectedRows.size === 0) {
      alert('Please select rows to replace');
      return;
    }
    
    // Filter document data to only include selected rows
    const filteredDocumentData = filterRowsBySelection(documentTargetData2D, selectedRows);
    
    if (filteredDocumentData.length <= 1) {
      alert('No valid document rows selected');
      return;
    }
    
    // For Document→Study, both are using the same project structure keys
    // Build key mapping from target structure (they should be identical)
    const keyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, documentTargetData2D[0], referenceId);
    
    // References don't use grouperColumns - use empty array (matching uses all matching keys)
    const grouperColumns: string[] = []; // References don't have grouper columns
    // Use key-based matching that updates existing AND adds new rows
    studyTargetData2D = matchSelectedSyncTargetToTarget(filteredDocumentData, studyTargetData2D, grouperColumns, keyMapping);
    updateStudyTargetGrid();
    // Update the study extract store to persist changes
    updateStudyExtractStore(studyTargetData2D);
  }
  
  function handleDocumentStudyMatching() {
    // Build key mapping from target structure
    const keyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, documentTargetData2D[0], referenceId);
    
    // References don't use grouperColumns - use empty array (matching uses all matching keys)
    const grouperColumns: string[] = []; // References don't have grouper columns
    studyTargetData2D = matchOnlySyncTargetToTarget(documentTargetData2D, studyTargetData2D, grouperColumns, keyMapping);
    updateStudyTargetGrid();
    // Update the study extract store to persist changes
    updateStudyExtractStore(studyTargetData2D);
  }
  
  function handleDocumentStudyAll() {
    // Build key mapping from target structure
    const keyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, documentTargetData2D[0], referenceId);
    
    studyTargetData2D = matchAllSyncTargetToTarget(documentTargetData2D, studyTargetData2D, keyMapping);
    updateStudyTargetGrid();
    // Update the study extract store to persist changes
    updateStudyExtractStore(studyTargetData2D);
  }
  
  // Pivot functionality - calls the pivotTable function and reloads the grid
  // Pivot functionality moved to SourceTableEditor
  
  // Component mounted
  onMount(() => {
  });
  
  // Cleanup on destroy
  onDestroy(() => {
    // SourceTableEditor manages its own grid cleanup
    if (documentTargetGridInstance) {
      documentTargetGridInstance.destroy();
    }
    if (studyTargetGridInstance) {
      studyTargetGridInstance.destroy();
    }
    // AG-Grid handles its own cleanup
  });
</script>

<!-- Reference Table Editor - Embedded Content -->
<div class="editor-content">
  
  <div class="editor-header">
    <span class="flex items-center gap-5S">
      <div class="editor-header-container">
        <span class="editor-header-label">Table:</span>
        <h2 class="editor-header-title">{tableDisplayName}</h2>
      </div>
      <div class="mode-selection-group">
        <div class="editor-button-group" role="group">
          <button 
            type="button" 
            class="editor-button-group-item editor-button-group-item-first {currentMode === 'source-document' ? 'editor-button-group-item-active' : ''}"
            on:click={() => handleModeChange('source-document')}
          >
            Source → Document
          </button>
          <!-- <button 
            type="button" 
            class="editor-button-group-item editor-button-group-item-middle {currentMode === 'source-study' ? 'editor-button-group-item-active' : ''}"
            on:click={() => handleModeChange('source-study')}
          >
            Source → Study
          </button> -->
          <button 
            type="button" 
            class="editor-button-group-item editor-button-group-item-last {currentMode === 'document-study' ? 'editor-button-group-item-active' : ''}"
            on:click={() => handleModeChange('document-study')}
          >
            Document → Study
          </button>
        </div>
      </div>
    </span>
    <div class="editor-actions">
      <Button class="save-button" size="xs" color="primary" on:click={handleSaveAndClose}>
        <FontAwesomeIcon icon={faSave} class="mr-2" />Save & Close
      </Button>
      <Button class="cancel-button" size="xs" color="light" on:click={handleCancel}>
        <FontAwesomeIcon icon={faTimes} class="mr-2" />Cancel
      </Button>
    </div>
  </div>

  <div class="editor-container">

    <!-- SOURCE → DOCUMENT Mode -->
    {#if currentMode === 'source-document'}
      <!-- SOURCE Section -->
      <SourceTableEditor
        availableSourceTables={availableSourceTables}
        bind:selectedSourceTable={selectedSourceTable}
        sourceData2D={sourceData2D}
        workingData={workingData}
        currentTableIndex={currentTableIndex}
        effectiveTableId={effectiveTableId}
        isReferenceMode={isReferenceMode}
        topicId={topicId}
        referenceId={referenceId}
        projectStructureStore={$projectStructureStore}
        columnAddEnabled={columnAddEnabled}
        rowRemoveEnabled={rowRemoveEnabled}
        selectedButtonEnabled={selectedButtonEnabled}
        matchingButtonEnabled={matchingButtonEnabled}
        bind:selectedRows={selectedRows}
        bind:selectedColumns={selectedColumns}
        bind:sourceGridInitialized={sourceGridInitialized}
        isUpdatingCell={isUpdatingCell}
        show={show}
        convertToGridFormat={convertToGridFormat}
        getCurrentVersion={getCurrentVersion}
        updateWorkingDataFromGrid={updateWorkingDataFromGrid}
        runValidation={runValidation}
        onSourceTableChange={(table) => { 
          // Note: selectedSourceTable is already updated via bind:value, but we ensure
          // the reactive statement detects the change by checking if it's different
          if (lastSelectedSourceTable !== table) {
            lastSelectedSourceTable = null; // Force reactive statement to trigger
            selectedSourceTable = table; // Ensure it's set (redundant but safe)
          }
        }}
        onAddColumn={noopWithEvent}
        onSaveColumn={handleSaveColumn}
        onRemoveColumn={handleRemoveColumn}
        onAddRow={noop}
        onRemoveRow={noop}
        onPivotTable={noop}
        onResetTable={noop}
        onSaveWorkingData={saveWorkingData}
        onRefreshSourceData={handleRefreshSourceData}
        onMatchSelected={handleMatchSelected}
        onMatchOnly={handleMatchOnly}
        onMatchAll={handleMatchAll}
        onSelectionChanged={handleSourceSelectionChanged}
        onGridReady={handleSourceGridReady}
      />
      
      <!-- DOCUMENT TARGET Section -->
      <div class="target-section">
        <div class="section-header">
          <span class="section-label">DOCUMENT TARGET</span>
          <!-- <span class="table-name">{effectiveTableId.toUpperCase()}</span> -->
        </div>
        
        <div class="tool-controls">
          <div class="tool-group">
            <div class="tool-buttons">
              <Button class="tool-button" size="xs" color="light" on:click={handleTargetAddRow} title="Add Row">
                <CustomImage id="table-row-add" size={24} />
              </Button>
              <Button class="tool-button" size="xs" color="light" disabled={!documentTargetRowRemoveEnabled} on:click={handleTargetRemoveRow} title="Remove Row">
                <CustomImage id="table-row-remove" size={24} />
              </Button>
            </div>
          </div>
          
            <div class="tool-group">
            <div class="tool-buttons">
              <!-- Reset Table button hidden for now -->
              <!-- <Button size="xs" color="primary" on:click={handleTargetTableReset}>Reset Table</Button> -->
            </div>
          </div>
        </div>
        
        <div class="grid-wrapper">
          <div 
            bind:this={documentTargetGridDiv}
            class="ag-theme-quartz ag-target-grid"
            data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"
          >          </div>
        </div>
      </div>
    {/if}

    <!-- SOURCE → STUDY Mode -->
    {#if currentMode === 'source-study'}
      <!-- SOURCE Section -->
      <SourceTableEditor
        availableSourceTables={availableSourceTables}
        bind:selectedSourceTable={selectedSourceTable}
        sourceData2D={sourceData2D}
        workingData={workingData}
        currentTableIndex={currentTableIndex}
        effectiveTableId={effectiveTableId}
        isReferenceMode={isReferenceMode}
        topicId={topicId}
        referenceId={referenceId}
        projectStructureStore={$projectStructureStore}
        columnAddEnabled={columnAddEnabled}
        rowRemoveEnabled={rowRemoveEnabled}
        selectedButtonEnabled={selectedButtonEnabled}
        matchingButtonEnabled={matchingButtonEnabled}
        bind:selectedRows={selectedRows}
        bind:selectedColumns={selectedColumns}
        bind:sourceGridInitialized={sourceGridInitialized}
        isUpdatingCell={isUpdatingCell}
        show={show}
        convertToGridFormat={convertToGridFormat}
        getCurrentVersion={getCurrentVersion}
        updateWorkingDataFromGrid={updateWorkingDataFromGrid}
        runValidation={runValidation}
        onSourceTableChange={(table) => { 
          // Note: selectedSourceTable is already updated via bind:value, but we ensure
          // the reactive statement detects the change by checking if it's different
          if (lastSelectedSourceTable !== table) {
            lastSelectedSourceTable = null; // Force reactive statement to trigger
            selectedSourceTable = table; // Ensure it's set (redundant but safe)
          }
        }}
        onAddColumn={noopWithEvent}
        onSaveColumn={handleSaveColumn}
        onRemoveColumn={handleRemoveColumn}
        onAddRow={noop}
        onRemoveRow={noop}
        onPivotTable={noop}
        onResetTable={noop}
        onSaveWorkingData={saveWorkingData}
        onRefreshSourceData={handleRefreshSourceData}
        onMatchSelected={handleMatchSelected}
        onMatchOnly={handleMatchOnly}
        onMatchAll={handleMatchAll}
        onSelectionChanged={handleSourceSelectionChanged}
        onGridReady={handleSourceGridReady}
      />
  
      <!-- STUDY TARGET Section -->
      <div class="target-section">
        <div class="section-header">
              <span class="section-label">STUDY TARGET:</span>
          <span class="table-name">{tableId.toUpperCase()}</span>
        </div>
        
        <div class="tool-controls">
          <div class="tool-group">
            <div class="tool-buttons">
              <Button class="tool-button" size="xs" color="primary" on:click={handleStudyTargetAddRow} title="Add Row">
                <CustomImage id="table-row-add" size={24} />
              </Button>
              <Button class="tool-button" size="xs" color="primary" disabled={!studyTargetRowRemoveEnabled} on:click={handleStudyTargetRemoveRow} title="Remove Row">
                <CustomImage id="table-row-remove" size={24} />
              </Button>
            </div>
          </div>
          
          <div class="tool-group">
            <div class="tool-buttons">
                  <!-- Reset Table button hidden for now -->
                <!-- <Button size="xs" color="primary" on:click={handleTargetTableReset}>Reset Table</Button> -->
            </div>
          </div>
        </div>
        
          <div class="grid-wrapper">
            {#if isReferenceMode && referenceId && studyTargetData2D.length > 0 && referenceColumnStructure && referenceColumnStructure.length > 0}
              <ReferenceGrid
                data2D={studyTargetData2D}
                columnStructure={referenceColumnStructure}
                rowIdMap={studyTargetRowIdMap}
                referenceId={referenceId}
                showColumnKeys={true}
                enableRowSelection={true}
                domLayout="normal"
                showIndicators={true}
                getSourceInfoForCell={getStudyTargetSourceInfoForCell}
                onCellValueChanged={handleStudyTargetCellValueChanged}
                onSelectionChanged={handleStudyTargetSelectionChanged}
                onGridReady={handleStudyTargetGridReady}
              />
            {:else}
              <!-- Fallback: keep old grid for topic mode (shouldn't happen in ReferenceTableEditor) -->
              <div 
                bind:this={studyTargetGridDiv}
                class="ag-theme-quartz ag-target-grid"
                data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"
              ></div>
            {/if}
          </div>
      </div>
    {/if}

    <!-- DOCUMENT → STUDY Mode -->
    {#if currentMode === 'document-study'}
      <div class="document-study-container">
        <!-- DOCUMENT TARGET Section -->
        <div class="target-section">
          <div class="section-header">
            <span class="section-label">DOCUMENT TARGET</span>
            <!-- <span class="table-name">{effectiveTableId.toUpperCase()}</span> -->
          </div>
          
          <div class="tool-controls">
            <div class="tool-group">
              <div class="tool-buttons">
                <Button class="tool-button" size="xs" color="light" on:click={handleDocumentTargetAddRow} title="Add Row">
                  <CustomImage id="table-row-add" size={24} />
                </Button>
                <Button class="tool-button" size="xs" color="light" disabled={!documentTargetRowRemoveEnabled} on:click={handleDocumentTargetRemoveRow} title="Remove Row">
                  <CustomImage id="table-row-remove" size={24} />
                </Button>
              </div>
            </div>
            
            <div class="tool-group">
              <div class="tool-buttons">
                <!-- Reset Table button hidden for now -->
                <!-- <Button size="xs" color="primary" on:click={handleDocumentTargetTableReset}>Reset Table</Button> -->
              </div>
            </div>
          </div>
          
          <div class="grid-wrapper">
            <div 
              bind:this={documentTargetGridDiv}
              class="ag-theme-quartz ag-target-grid"
              data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"
            ></div>
          </div>
        </div>
        
        <!-- Replace Action Area -->
        <div class="replace-actions">
          <span class="replace-label">Replace:</span>
          <div class="replace-buttons">
            <Button size="xs" color="primary" disabled={!documentStudySelectedButtonEnabled} on:click={handleDocumentStudySelected}>
              <FontAwesomeIcon icon={faArrowDown} class="mr-2" />
              Selected
            </Button>
            <span class="replace-label">OR</span>           
            <Button size="xs" color="primary" on:click={handleDocumentStudyMatching}>
              <FontAwesomeIcon icon={faArrowDown} class="mr-2" />
              Matching
            </Button>
            <span class="replace-label">OR</span>           
            <Button size="xs" color="primary" on:click={handleDocumentStudyAll}>
              <FontAwesomeIcon icon={faArrowDown} class="mr-2" />
              All
            </Button>
          </div>
        </div>
        
        <!-- STUDY TARGET Section -->
        <div class="target-section">
          <div class="section-header">
            <span class="section-label">STUDY TARGET</span>
            <!-- <span class="table-name">{effectiveTableId.toUpperCase()}</span> -->
          </div>
          
          <div class="tool-controls">
            <div class="tool-group">
              <div class="tool-buttons">
                <Button class="tool-button" size="xs" color="light" on:click={handleStudyTargetAddRow} title="Add Row">
                  <CustomImage id="table-row-add" size={24} />
                </Button>
                <Button class="tool-button" size="xs" color="light" disabled={!studyTargetRowRemoveEnabled} on:click={handleStudyTargetRemoveRow} title="Remove Row">
                  <CustomImage id="table-row-remove" size={24} />
                </Button>
              </div>
            </div>
            
            <div class="tool-group">
              <div class="tool-buttons">
                <!-- Reset Table button hidden for now -->
                <!-- <Button size="xs" color="primary" on:click={handleStudyTargetTableReset}>Reset Table</Button> -->
              </div>
            </div>
          </div>
          
          <div class="grid-wrapper">
            {#if isReferenceMode && referenceId && studyTargetData2D.length > 0 && referenceColumnStructure && referenceColumnStructure.length > 0}
              <ReferenceGrid
                data2D={studyTargetData2D}
                columnStructure={referenceColumnStructure}
                rowIdMap={studyTargetRowIdMap}
                referenceId={referenceId}
                showColumnKeys={true}
                enableRowSelection={true}
                domLayout="normal"
                showIndicators={true}
                getSourceInfoForCell={getStudyTargetSourceInfoForCell}
                onCellValueChanged={handleStudyTargetCellValueChanged}
                onSelectionChanged={handleStudyTargetSelectionChanged}
                onGridReady={handleStudyTargetGridReady}
              />
            {:else}
              <!-- Fallback: keep old grid for topic mode (shouldn't happen in ReferenceTableEditor) -->
              <div 
                bind:this={studyTargetGridDiv}
                class="ag-theme-quartz ag-target-grid"
                data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"
              ></div>
            {/if}
          </div>
        </div>
      </div>
    {/if}
    
  </div>

</div>
